"use client";

import useServiceCatalogBenefits from "@/app/booking/rewards/useServiceCatalogBenefits";
import { projectServiceCatalogSummaryBenefits } from "@/app/booking/rewards/serviceCatalogSummaryBenefits";
import type { ServiceCatalogBenefitsContextV1 } from "@/app/booking/rewards/serviceCatalogBenefits";
import usePackageCatalogBenefits from "@/app/commerce/packages/usePackageCatalogBenefits";
import type { AppointmentResourceOnlyExecutionV1 } from "@/app/booking/core/appointmentResourceOnlyExecutionV1";

import { useRouter } from "next/navigation";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocale, useTranslations } from "next-intl";

import type { AppointmentDraftMode } from "@/app/booking/core/appointmentBookingDraft";
import { FRONTEND_ACTIVITY_PROVIDER_ID } from "@/app/activity/domain/frontendFixtureActivityProvider";
import { isUnconfiguredPackageSessionServiceSelection } from "@/app/booking/packages/packageSessionBookingSelection";
import type { MembershipSessionServicesPresentation } from "@/app/booking/memberships/membershipSessionServicesPreview";
import type { ServiceSessionPresentation } from "@/app/specialists/serviceSessionPresentation";
import { getServiceSessionDemoBadgeLabel, getServiceSessionDemoPriceLabel } from "@/app/specialists/serviceSessionDemoFixtures";
import { isSpecialistStepOneFeasible } from "@/app/booking/core/specialistStepOneFeasibility";
import BookingFlowSelectionFooter from "@/app/booking/components/BookingFlowSelectionFooter";
import AppointmentGroupParticipantCard from "@/app/booking/components/AppointmentGroupParticipantCard";
import { createFrontendDescendantStateOwnerKey } from "@/app/hooks/frontendDescendantStateOwner";
import BookingOptionCard from "@/app/components/BookingOptionCard";
import BookingOptionIcon from "@/app/components/BookingOptionIcon";
import {
  BOOKING_OPTION_COPY,
  getRemainingBookingOptions,
} from "@/app/booking/presentation/bookingOptionPresentation";
import ForwardArrowIcon from "@/app/components/ForwardArrowIcon";
import { useHorizontalOverflowRail } from "@/app/hooks/useHorizontalOverflowRail";
import { buildLocationProfilePath } from "@/app/locations/routes";
import {
  formatResponsiveBookingDuration,
  formatResponsiveBookingDurationRange,
} from "@/app/components/booking-flow/responsiveBookingDuration";
import type { LocationServicePortfolioImage } from "@/app/locations/locationServicePortfolio";
import {
  EMPTY_LOCATION_SERVICE_REVIEW_SUMMARIES,
  getPublishedLocationServiceReviewSummary,
  type PublishedLocationServiceReviewSummary,
} from "@/app/locations/locationServiceReviewSummary";
import CheckoutConfirmationModal from "@/app/components/CheckoutConfirmationModal";
import ModalShell, {
  type ModalDismissReason,
} from "@/app/components/ModalShell";
import SelectDropdown from "@/app/components/SelectDropdown";
import SegmentedControl from "@/app/components/SegmentedControl";
import ToolbarPillButton from "@/app/components/ToolbarPillButton";
import VoleraButton from "@/app/components/VoleraButton";
import { DataStateBoundary } from "@/app/components/loading/SectionStates";
import {
  SkeletonButton,
  SkeletonCard,
  SkeletonTextLine,
} from "@/app/components/loading/Skeleton";
import SpecialistBookingSummary, {
  type SpecialistBookingSummaryItem,
} from "@/app/specialists/components/SpecialistBookingSummary";
import SpecialistPackageCard from "@/app/specialists/components/SpecialistPackageCard";
import SpecialistPackageUpsellCard from "@/app/specialists/components/SpecialistPackageUpsellCard";
import SpecialistPackageDetailsModal from "@/app/specialists/components/SpecialistPackageDetailsModal";
import { isPackageCatalogDefinitionV1, isPackageCatalogDefinitionV2, type PackageCatalogDefinition } from "@/app/commerce/packages/packageCatalogDefinition";
import SpecialistServiceCard from "@/app/specialists/components/SpecialistServiceCard";
import SpecialistServiceBundleConfigurationModal from "@/app/specialists/components/SpecialistServiceBundleConfigurationModal";
import SpecialistServiceConfigurationModal from "@/app/specialists/components/SpecialistServiceConfigurationModal";
import SpecialistServiceDetailsModal from "@/app/specialists/components/SpecialistServiceDetailsModal";
import type { SpecialistWorkplacePresentation } from "@/app/specialists/components/SpecialistWorkplaceCard";
import {
  resolveSpecialistCatalogCategoryDisplayLabel,
  type SpecialistCatalogCategory,
} from "@/app/specialists/catalogCategories";
import type {
  SpecialistAssignment,
  SpecialistPerson,
  SpecialistPrice,
  SpecialistService,
  SpecialistServiceAddOnSelection,
  SpecialistServiceBundleItemSelection,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import {
  SPECIALIST_FEATURED_CATEGORY_ID,
  getFeaturedSpecialistServices,
} from "@/app/specialists/featuredServices";
import {
  getSpecialistGroupBookingPolicy,
  isSpecialistOfferingPublishedAtWorkplace,
} from "@/app/specialists/onlineBooking";
import {
  getSpecialistPackageSale,
  getSpecialistPackageUpsellForService,
} from "@/app/specialists/packages";
import type {
  SpecialistCatalogFocusTarget,
  SpecialistCatalogView,
  SpecialistServiceConfigurationTarget,
} from "@/app/specialists/routes";
import { isSpecialistServiceConfigurationAvailableAtWorkplace } from "@/app/specialists/serviceConfigurationAvailability";
import { getSpecialistServiceBundle } from "@/app/specialists/serviceBundles";
import {
  createSpecialistServiceBundleAddOnPlan,
  resolveSpecialistServiceBundleConfiguration,
  type SpecialistServiceBundleAddOnPlan,
} from "@/app/specialists/serviceBundleAddOns";
import { formatSpecialistCurrencyAmount } from "@/app/specialists/priceFormatting";
import {
  isValidSpecialistPrice,
  specialistPriceAmountToMinorUnits,
  specialistPriceMinorUnitsToAmount,
} from "@/app/specialists/priceSemantics";
import { formatSpecialistServiceAudienceLabel } from "@/app/specialists/serviceAudiencePresentation";
import { resolveServiceReviewsContextKindFromEntryPoint } from "@/app/specialists/serviceReviewsContext";
import {
  getSelectableSpecialistServiceVariant as getBookableVariant,
  specialistOfferingRequiresVariantSelection,
} from "@/app/specialists/serviceVariants";
import {
  resolveSpecialistServiceConfiguration,
  specialistOfferingRequiresAddOnConfiguration,
} from "@/app/specialists/serviceAddOns";
import {
  SPECIALIST_SERVICE_COLLECTION_ORDER,
  getSpecialistServiceSale,
} from "@/app/specialists/servicePromotions";
import { getServiceCatalogCompareAtAmountMinor } from '@/app/booking/rewards/serviceCatalogCompareAt';

import styles from "./SpecialistServicesModal.module.css";

export type SpecialistServicesDataState = "ready" | "loading" | "error";
export type SpecialistServicesPresentation = "modal" | "page";
export type SpecialistServicesPageExitAction = "back" | "close";
export type SpecialistServicesEntryPointKind =
  "specialist-services" | "location-services";
export type SpecialistInitialConfigurationOutcome =
  "committed" | "opened" | "rejected";

/**
 * Presentation-only projection of the default fixture's unconfigured service
 * coverage. The route exact-binds the provider, service and offering; another
 * provider cannot borrow this prospective total/badge without its own contract.
 * This selector never treats display copy as eligibility.
 */
export interface SpecialistServiceBenefitPresentation {
  providerId: string;
  serviceId: string;
  offeringId: string;
  priceLabel: string;
}
const EMPTY_SERVICE_IDS: readonly string[] = [];
const EMPTY_PACKAGE_IDS: readonly string[] = [];
const EMPTY_PACKAGES: readonly PackageCatalogDefinition[] = [];
const EMPTY_SERVICE_SELECTIONS: readonly SpecialistServiceSelection[] = [];

export function resolveSpecialistServicesReviewScope(input: {
  entryPointKind?: SpecialistServicesEntryPointKind;
  assignmentUserLocationId: string;
}) {
  const entryPointKind =
    input.entryPointKind ?? "specialist-services";
  const contextKind = resolveServiceReviewsContextKindFromEntryPoint(
    entryPointKind,
  );

  return Object.freeze({
    entryPointKind,
    contextKind,
    fixedUserLocationId:
      contextKind === "service-specialist"
        ? input.assignmentUserLocationId
        : null,
  });
}

export function resolveServicesCatalogPresentation(input: {
  entryPointKind: SpecialistServicesEntryPointKind;
  personName: string;
  workplaceName: string;
  anyProfessionalLabel: string;
  initialProfessionalName?: string | null;
}) {
  return input.entryPointKind === "location-services"
    ? {
        catalogSubjectName: input.workplaceName,
        serviceProviderName:
          input.initialProfessionalName ?? input.anyProfessionalLabel,
      }
    : {
        catalogSubjectName: input.personName,
        serviceProviderName: input.personName,
      };
}
const RESPONSIVE_SERVICES_PAGE_QUERY = "(max-width: 1199px)";
const SELECTOR_SCROLL_BODY_SELECTOR =
  "[data-specialist-services-scroll-body], [data-modal-scroll-body]";
const CATEGORY_ACTIVATION_GAP = 8;
const CATEGORY_SCROLL_END_TOLERANCE = 2;
const CATEGORY_OVERFLOW_TOLERANCE = 1;
const CATEGORY_STICKY_DOCK_TOLERANCE = 1;
const CATEGORY_RAIL_SETTLE_DELAY = 120;
const CATEGORY_EDGE_FADE_WIDTH = 44;
const PROGRAMMATIC_CATEGORY_SCROLL_TIMEOUT = 900;
const BUNDLE_CATEGORY_ID = "bundles";
const SELECTED_SERVICE_VISIBILITY_TOLERANCE = 1;
const SELECTED_SERVICE_NAVIGATION_EDGE_GAP = 10;
const SELECTED_SERVICE_NAVIGATION_SETTLE_DELAY = 140;
const SELECTED_SERVICE_NAVIGATION_SCROLL_FALLBACK_DELAY = 1_000;

type SelectedServiceNavigationDirection = "above" | "below";

interface SelectedServiceNavigationSnapshot {
  aboveCount: number;
  aboveTargetId: string | null;
  belowCount: number;
  belowTargetId: string | null;
  left: number;
  top: number;
  width: number;
}

interface ActiveServiceDetailsState {
  interactionToken: number;
  serviceId: string;
  isOpen: boolean;
  draftVariantId?: string | null;
  draftAddOns?: readonly SpecialistServiceAddOnSelection[];
  draftBundleItems?: readonly SpecialistServiceBundleItemSelection[];
}

export function isServicesCategoryNavigationDocked(input: {
  navigationTop: number;
  scrollBodyTop: number;
  position: string;
}) {
  if (
    input.position !== "sticky" ||
    !Number.isFinite(input.navigationTop) ||
    !Number.isFinite(input.scrollBodyTop)
  ) {
    return false;
  }

  return (
    input.navigationTop <= input.scrollBodyTop + CATEGORY_STICKY_DOCK_TOLERANCE
  );
}

interface ActiveServiceConfigurationState {
  interactionToken: number;
  kind: "bundle" | "service";
  initialStep: "variants" | "add-ons";
  initialVariantId: string | null;
  initialAddOns?: readonly SpecialistServiceAddOnSelection[];
  initialBundleItems?: readonly SpecialistServiceBundleItemSelection[];
  returnToDetails: boolean;
  serviceId: string;
  isOpen: boolean;
}

type PendingServiceSiblingTransition =
  | {
      catalogOwnerKey: string;
      fromInteractionToken: number;
      kind: "configuration";
      sourceKind: "configuration" | "details";
      target: ActiveServiceConfigurationState;
      transitionToken: number;
    }
  | {
      catalogOwnerKey: string;
      fromInteractionToken: number;
      kind: "details";
      sourceKind: "configuration" | "details";
      target: ActiveServiceDetailsState;
      transitionToken: number;
    };

interface ActivePackageDetailsState {
  packageId: string;
  isOpen: boolean;
}

interface BeginServiceConfigurationInput {
  event?: MouseEvent<HTMLButtonElement>;
  initialVariantId?: string | null;
  returnFocusTarget?: HTMLElement | null;
  returnToDetails?: boolean;
  serviceId: string;
}

interface SpecialistServicesModalProps {
  appointmentMode?: AppointmentDraftMode;
  /**
   * Optional owner for a transactional parent catalog (for example one Group
   * participant). It joins every nested Details/configuration publication to
   * that parent. A caller that changes participant must additionally key this
   * complete catalog by the same value so route-local React state remounts
   * synchronously instead of crossing participant owners.
   */
  catalogSelectionOwnerKey?: string | null;
  /** Presentation only; the parent owns the exact Group participant. */
  groupParticipantName?: string | null;
  groupParticipantIsGuest?: boolean;
  /** Exact other participants for display while this participant edits locally. */
  groupSummaryContext?: {
    participantId: string;
    participantOrder: readonly string[];
    /** The committed receipt stays visible until the new guest selects a service. */
    pendingGuestSummary?: { totalValueLabel: string };
    others: readonly {
      participantId: string;
      items: readonly SpecialistBookingSummaryItem[];
      totalAmountMinor: number;
      currency: string;
      durationMinutes: number;
      durationMaxMinutes: number;
      hasStartingPrice: boolean;
    }[];
  } | null;
  /** Reveal the first selected catalog row once when an edit transaction mounts. */
  revealSelectedServiceOnMount?: boolean;
  /**
   * Transactional parent catalogs can cancel their memory-only child directly;
   * the parent remains the sole committed authority and needs no route-leave
   * confirmation for that child dismissal.
   */
  confirmSelectionOnDismiss?: boolean;
  dataState?: SpecialistServicesDataState;
  /**
   * Identifies the document that owns this shared catalog. Location entry
   * keeps the catalog owner internal and presents unresolved staff as
   * "Any professional" until the next booking step.
   */
  entryPointKind?: SpecialistServicesEntryPointKind;
  isOpen: boolean;
  presentation?: SpecialistServicesPresentation;
  person: SpecialistPerson | null;
  locationResourceOnlyExecutionsV1?: readonly AppointmentResourceOnlyExecutionV1[];
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  /** Exact-ID marketplace presentation seam; null keeps the initials fallback. */
  workplacePresentation?: SpecialistWorkplacePresentation | null;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
  serviceReviewSummaries?: readonly PublishedLocationServiceReviewSummary[];
  servicePortfolioImages?: readonly LocationServicePortfolioImage[];
  packages?: readonly PackageCatalogDefinition[];
  /** Presentation gate only. The route still owns fresh versioned handoff authority. */
  freshLocationPackagePurchase?: boolean;
  canPurchasePackage?: (packageItem: PackageCatalogDefinition) => boolean;
  /** Provider-authored category copy/order, exact-validated at the route boundary. */
  serviceCategoryPublication: readonly SpecialistCatalogCategory[];
  packageCategoryPublication: readonly SpecialistCatalogCategory[];
  catalogView?: SpecialistCatalogView;
  /** Keeps the shared Package catalog inside the explicit gift selection. */
  giftPackageCatalog?: boolean;
  /**
   * One-shot page-route handoff. The page validates this against its active
   * URL-owned selection before the selector reveals and focuses the item.
   */
  initialFocusTarget?: SpecialistCatalogFocusTarget | null;
  /**
   * One-shot, unconfirmed handoff owned by the page route. The selector
   * reveals the first deterministic service copy and starts its existing
   * coordinator without first publishing a parent-only selection.
   */
  initialConfigurationTarget?: SpecialistServiceConfigurationTarget | null;
  /**
   * Location-owned initial preference carried from an embedded employee
   * profile. It changes summary copy only; scheduling remains editable.
   */
  initialProfessionalName?: string | null;
  /** Per-service Rebook copy, joined to the current catalog by the route. */
  initialServiceProfessionalNames?: Readonly<Record<string, string>>;
  /** Exact route-owned benefit projection; the covered row contributes zero. */
  serviceBenefitPresentation?: SpecialistServiceBenefitPresentation | null;
  /** Provider-free Membership projection, independent of Package benefit rules. */
  membershipSessionPresentation?: MembershipSessionServicesPresentation | null;
  /** Explicit domain-owned configured base/remainder display, never authority. */
  serviceSessionPresentation?: ServiceSessionPresentation | null;
  serviceBenefitsContext?: ServiceCatalogBenefitsContextV1;
  /** An explicit unavailable continuation never disables cart editing or dismissal. */
  continuationUnavailableLabel?: string | null;
  /** Canonical committed parent + exact-variant selections. */
  selectedServices?: readonly SpecialistServiceSelection[];
  /** @deprecated Parent-only compatibility seam while callers migrate. */
  selectedServiceIds?: readonly string[];
  selectedPackageIds?: readonly string[];
  /** Route-owned exact capability gate; workplace policy alone is insufficient. */
  groupBookingAvailable?: boolean;
  unavailableServiceIds?: readonly string[];
  isContinuing: boolean;
  returnFocusRef: RefObject<HTMLElement | null>;
  onSelectedServicesChange?: (
    selections: SpecialistServiceSelection[],
  ) => boolean | void | Promise<boolean | void>;
  /** @deprecated Parent-only compatibility seam while callers migrate. */
  onSelectedServiceIdsChange?: (serviceIds: string[]) => void;
  onSelectedPackageIdsChange?: (
    packageIds: string[],
  ) => boolean | void | Promise<boolean | void>;
  onCatalogViewChange?: (
    view: SpecialistCatalogView,
  ) => boolean | void | Promise<boolean | void>;
  onAppointmentModeChange?: (
    mode: AppointmentDraftMode,
  ) => boolean | void | Promise<boolean | void>;
  productsHref?: string;
  membershipsHref?: string;
  giftCardsHref?: string;
  /** Optional side-effect seam (for example analytics); navigation stays link-owned. */
  onProductsAction?: () => void;
  /** Optional side-effect seam (for example analytics); navigation stays link-owned. */
  onMembershipsAction?: () => void;
  /** Optional side-effect seam (for example analytics); navigation stays link-owned. */
  onGiftCardsAction?: () => void;
  onInitialFocusHandled?: () => void;
  onInitialConfigurationHandled?: (
    outcome: SpecialistInitialConfigurationOutcome,
  ) => void;
  onContinue: (selections: readonly SpecialistServiceSelection[]) => void;
  onContinuePackages?: (packageIds: readonly string[]) => void;
  onClose: () => void;
  onPageExit?: (action: SpecialistServicesPageExitAction) => void;
  onDismiss?: (reason: ModalDismissReason) => void;
  onRetry?: () => void;
  onServiceSelectionChange?: (change: {
    action: "deselected" | "selected";
    selectedServiceCount: number;
    serviceId: string;
  }) => void;
}

type SpecialistServicesDismissReason =
  ModalDismissReason | "page-back" | "page-close" | "workplace-navigation";

function ServicesPageBackIcon() {
  return (
    <>
      <svg
        className={styles.pageBackIconCompact}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M15 5 8 12l7 7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        className={styles.pageBackIconExpanded}
        viewBox="0 0 1024 1024"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z"
        />
        <path
          fill="currentColor"
          d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z"
        />
      </svg>
    </>
  );
}

function ServicesPageCloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Keep the last visible copy mounted while the pill scales out. */
function SelectedServicesNavigator({
  children,
  direction,
  visible,
}: {
  children: ReactNode;
  direction: SelectedServiceNavigationDirection;
  visible: boolean;
}) {
  const lastVisibleContent = useRef<ReactNode>(null);
  useLayoutEffect(() => {
    if (visible) lastVisibleContent.current = children;
  }, [children, visible]);

  return (
    <div
      className={styles.selectedServicesNavigator}
      data-direction={direction}
      data-visible={visible ? "true" : undefined}
      aria-hidden={!visible}
      inert={!visible}
    >
      {visible ? children : lastVisibleContent.current}
    </div>
  );
}

interface SpecialistServicesPresentationShellProps {
  children: ReactNode;
  closeLabel: string;
  isCondensedHeader: boolean;
  isDismissDisabled: boolean;
  isOpen: boolean;
  onBeforeDismiss: (reason: ModalDismissReason) => boolean;
  onClose: () => void;
  onDismiss?: (reason: ModalDismissReason) => void;
  onPageExit: (action: SpecialistServicesPageExitAction) => void;
  pageBackLabel: string;
  pageCloseLabel: string;
  pageHeaderRef: RefObject<HTMLElement | null>;
  presentation: SpecialistServicesPresentation;
  returnFocusRef: RefObject<HTMLElement | null>;
  scrollViewportOverlay?: ReactNode;
  footer?: ReactNode;
  title: string;
  titleId: string;
}

function SpecialistServicesPresentationShell({
  children,
  closeLabel,
  footer,
  isCondensedHeader,
  isDismissDisabled,
  isOpen,
  onBeforeDismiss,
  onClose,
  onDismiss,
  onPageExit,
  pageBackLabel,
  pageCloseLabel,
  pageHeaderRef,
  presentation,
  returnFocusRef,
  scrollViewportOverlay,
  title,
  titleId,
}: SpecialistServicesPresentationShellProps) {
  if (presentation === "modal") {
    return (
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        onDismiss={onDismiss}
        onBeforeDismiss={onBeforeDismiss}
        closeLabel={closeLabel}
        title={title}
        size="extraWide"
        layout="selector"
        titlePlacement="header"
        showHeaderDivider={false}
        responsiveMode="content-sheet"
        footerMode="actions"
        disableDesktopAutoSize
        isDismissDisabled={isDismissDisabled}
        returnFocusRef={returnFocusRef}
        overlayClassName={styles.servicesOverlay}
        headerClassName={styles.modalHeader}
        bodyClassName={styles.modalBody}
        footerClassName={styles.modalFooter}
        scrollViewportOverlay={scrollViewportOverlay}
        footer={footer}
      >
        {children}
      </ModalShell>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={styles.servicesPage}
      data-condensed-header={isCondensedHeader ? "true" : "false"}
      data-specialist-services-page=""
    >
      <header ref={pageHeaderRef} className={styles.pageHeader}>
        <button
          type="button"
          className={`${styles.pageHeaderButton} ${styles.pageBackButton}`}
          aria-label={pageBackLabel}
          disabled={isDismissDisabled}
          onClick={() => onPageExit("back")}
        >
          <ServicesPageBackIcon />
        </button>
        {/* Responsive condensed reveal shows the page title, matching the
            expanded header morph. */}
        <p aria-hidden={!isCondensedHeader}>{title}</p>
        <button
          type="button"
          className={`${styles.pageHeaderButton} ${styles.pageCloseButton}`}
          aria-label={pageCloseLabel}
          disabled={isDismissDisabled}
          onClick={() => onPageExit("close")}
        >
          <ServicesPageCloseIcon />
        </button>
        {/* Expanded-only decorative copy of the page title; the in-flow h1
            keeps the accessible name while this transmutes into the header
            with the home search morph's choreography. */}
        <div
          className={styles.pageHeaderTitleMount}
          data-specialist-header-title=""
          aria-hidden="true"
        >
          <span className={styles.pageHeaderTitleCopy}>{title}</span>
        </div>
      </header>

      <div className={styles.pageViewport}>
        <main
          className={styles.pageScrollBody}
          data-specialist-services-scroll-body=""
          aria-labelledby={titleId}
        >
          <div className={styles.pageContent}>
            <h1 id={titleId} className={styles.pageTitle}>
              {title}
            </h1>
            {children}
          </div>
        </main>
        {scrollViewportOverlay ? (
          <div className={styles.pageScrollViewportOverlay}>
            {scrollViewportOverlay}
          </div>
        ) : null}
      </div>

      {footer ? <footer className={styles.pageFooter}>{footer}</footer> : null}
    </div>
  );
}

function selectionKey(selection: SpecialistServiceSelection) {
  return JSON.stringify([
    selection.serviceId,
    selection.variantId ?? null,
    selection.addOns?.map(({ groupId, addOnId, quantity }) => [
      groupId,
      addOnId,
      quantity,
    ]) ?? [],
    selection.bundleItems?.map((bundleItem) => [
      bundleItem.bundleItemId,
      bundleItem.serviceId,
      bundleItem.variantId,
      bundleItem.addOns.map(({ groupId, addOnId, quantity }) => [
        groupId,
        addOnId,
        quantity,
      ]),
    ]) ?? [],
  ]);
}

function useIsResponsiveServicesPage() {
  const [isResponsive, setIsResponsive] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(RESPONSIVE_SERVICES_PAGE_QUERY);
    const syncResponsiveState = () => setIsResponsive(mediaQuery.matches);

    syncResponsiveState();
    mediaQuery.addEventListener("change", syncResponsiveState);

    return () => {
      mediaQuery.removeEventListener("change", syncResponsiveState);
    };
  }, []);

  return isResponsive;
}

function formatPrice(
  price: SpecialistPrice | null,
  locale: string,
  unavailableLabel: string,
  freeLabel: string,
) {
  if (!isValidSpecialistPrice(price)) {
    return unavailableLabel;
  }

  if (price.type === "free") {
    return freeLabel;
  }

  return formatSpecialistCurrencyAmount(locale, price.amount, price.currency);
}

export function resolveSpecialistSelectionTotal(
  prices: readonly (SpecialistPrice | null)[],
) {
  if (prices.length === 0) {
    return { amount: 0, currency: "RON" };
  }

  if (prices.some((price) => !isValidSpecialistPrice(price))) {
    return null;
  }

  const exactPrices = prices as readonly SpecialistPrice[];
  const currencies = new Set(
    exactPrices.map((price) => price.currency.trim().toUpperCase()),
  );
  if (currencies.size !== 1) return null;

  const currency = [...currencies][0]!;
  const amountsMinor = exactPrices.map((price) =>
    specialistPriceAmountToMinorUnits(price.amount, currency),
  );
  if (amountsMinor.some((amount) => amount === null)) return null;

  const totalMinor = (amountsMinor as number[]).reduce(
    (total, amount) => total + amount,
    0,
  );
  const amount = Number.isSafeInteger(totalMinor)
    ? specialistPriceMinorUnitsToAmount(totalMinor, currency)
    : null;

  return amount === null ? null : { amount, currency };
}

function ServiceNavigationArrow({
  className,
  direction,
}: {
  className?: string;
  direction: "above" | "below";
}) {
  return <ForwardArrowIcon className={className} data-direction={direction} />;
}

function isSameSelectedServiceNavigationSnapshot(
  current: SelectedServiceNavigationSnapshot | null,
  next: SelectedServiceNavigationSnapshot,
) {
  return (
    current?.aboveCount === next.aboveCount &&
    current.aboveTargetId === next.aboveTargetId &&
    current.belowCount === next.belowCount &&
    current.belowTargetId === next.belowTargetId &&
    Math.abs(current.left - next.left) <= 0.5 &&
    Math.abs(current.top - next.top) <= 0.5 &&
    Math.abs(current.width - next.width) <= 0.5
  );
}

export default function SpecialistServicesModal({
  appointmentMode = "individual",
  catalogSelectionOwnerKey = null,
  groupParticipantName = null,
  groupParticipantIsGuest = false,
  groupSummaryContext = null,
  revealSelectedServiceOnMount = false,
  confirmSelectionOnDismiss = true,
  dataState = "ready",
  entryPointKind: explicitEntryPointKind,
  isOpen,
  presentation = "modal",
  person,
  locationResourceOnlyExecutionsV1,
  assignment,
  workplace,
  workplacePresentation = null,
  services,
  offerings,
  serviceReviewSummaries = EMPTY_LOCATION_SERVICE_REVIEW_SUMMARIES,
  servicePortfolioImages = [],
  packages = EMPTY_PACKAGES,
  freshLocationPackagePurchase = false,
  canPurchasePackage = isPackageCatalogDefinitionV1,
  serviceCategoryPublication,
  packageCategoryPublication,
  catalogView = "services",
  giftPackageCatalog = false,
  initialFocusTarget = null,
  initialConfigurationTarget = null,
  initialProfessionalName = null,
  initialServiceProfessionalNames,
  serviceBenefitPresentation = null,
  membershipSessionPresentation = null,
  serviceSessionPresentation = null,
  serviceBenefitsContext,
  continuationUnavailableLabel = null,
  selectedServices,
  selectedServiceIds = EMPTY_SERVICE_IDS,
  selectedPackageIds = EMPTY_PACKAGE_IDS,
  groupBookingAvailable = false,
  unavailableServiceIds = EMPTY_SERVICE_IDS,
  isContinuing,
  returnFocusRef,
  onSelectedServicesChange,
  onSelectedServiceIdsChange,
  onSelectedPackageIdsChange,
  onCatalogViewChange,
  onAppointmentModeChange,
  giftCardsHref,
  membershipsHref,
  productsHref,
  onGiftCardsAction,
  onMembershipsAction,
  onProductsAction,
  onInitialFocusHandled,
  onInitialConfigurationHandled,
  onContinue,
  onContinuePackages,
  onClose,
  onPageExit,
  onDismiss,
  onRetry,
  onServiceSelectionChange,
}: SpecialistServicesModalProps) {
  const router = useRouter();
  const t = useTranslations("specialist");
  const tAll = useTranslations();
  const locale = useLocale();
  const packageBenefits = usePackageCatalogBenefits(packages, locale, { freshLocationPurchase: freshLocationPackagePurchase });
  const entryPointKind =
    explicitEntryPointKind ?? "specialist-services";
  const serviceReviewsScope = resolveSpecialistServicesReviewScope({
    entryPointKind,
    assignmentUserLocationId: assignment.userLocationId,
  });
  const isLocationEntryPoint = entryPointKind === "location-services";
  const serviceReviewsContextKind = serviceReviewsScope.contextKind;
  const fixedReviewUserLocationId =
    serviceReviewsScope.fixedUserLocationId;
  const workplaceHref = buildLocationProfilePath(workplace.locationId);
  const { catalogSubjectName, serviceProviderName } =
    resolveServicesCatalogPresentation({
      entryPointKind,
      personName: person?.name ?? "",
      workplaceName: workplace.name,
      anyProfessionalLabel: tAll("activity.fields.anyProfessional"),
      initialProfessionalName,
    });
  const isResponsiveServicesPage = useIsResponsiveServicesPage();
  const pageTitleId = useId();
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [pendingDismissReason, setPendingDismissReason] =
    useState<SpecialistServicesDismissReason | null>(null);
  const [isCondensedHeader, setIsCondensedHeader] = useState(false);
  const [isCategoryNavigationDocked, setIsCategoryNavigationDocked] =
    useState(false);
  const [selectedServiceNavigation, setSelectedServiceNavigation] =
    useState<SelectedServiceNavigationSnapshot | null>(null);
  const lastServiceNavigationGeometry =
    useRef<SelectedServiceNavigationSnapshot | null>(null);
  useLayoutEffect(() => {
    if (selectedServiceNavigation) {
      lastServiceNavigationGeometry.current = selectedServiceNavigation;
    }
  }, [selectedServiceNavigation]);
  const [activeServiceDetails, setActiveServiceDetails] =
    useState<ActiveServiceDetailsState | null>(null);
  const [activeServiceConfiguration, setActiveServiceConfiguration] =
    useState<ActiveServiceConfigurationState | null>(null);
  const [pendingServiceSiblingTransition, setPendingServiceSiblingTransition] =
    useState<PendingServiceSiblingTransition | null>(null);
  const [activePackageDetails, setActivePackageDetails] =
    useState<ActivePackageDetailsState | null>(null);
  const activeServiceDetailsRef = useRef<ActiveServiceDetailsState | null>(
    null,
  );
  const activeServiceConfigurationRef =
    useRef<ActiveServiceConfigurationState | null>(null);
  const serviceInteractionTokenRef = useRef(0);
  const pendingServiceSiblingTransitionRef =
    useRef<PendingServiceSiblingTransition | null>(null);
  const catalogDescendantOwnerKeyRef = useRef<string | null>(null);
  const servicesOwnerIsOpenRef = useRef(isOpen);
  const publishPendingServiceSiblingTransition = useCallback(
    (next: PendingServiceSiblingTransition | null) => {
      pendingServiceSiblingTransitionRef.current = next;
      setPendingServiceSiblingTransition(next);
    },
    [],
  );
  const [pendingPackageUpsellId, setPendingPackageUpsellId] = useState<
    string | null
  >(null);
  const [
    isSelectedServiceNavigationScrolling,
    setIsSelectedServiceNavigationScrolling,
  ] = useState(false);
  const servicesColumnRef = useRef<HTMLDivElement | null>(null);
  const pageHeaderRef = useRef<HTMLElement | null>(null);
  const categorySectionRefs = useRef(new Map<string, HTMLElement>());
  const pendingCategoryScrollRef = useRef<string | null>(null);
  const pendingCategoryScrollTimeoutRef = useRef<number | null>(null);
  const pendingSelectedServicesRef = useRef<
    readonly SpecialistServiceSelection[]
  >(selectedServices ?? EMPTY_SERVICE_SELECTIONS);
  const selectionMutationInFlightRef = useRef(false);
  const [isSelectionMutationPending, setIsSelectionMutationPending] =
    useState(false);
  const requestedSelectionNormalizationRef = useRef<string | null>(null);
  const selectedServiceNavigationTargetsRef = useRef<
    Record<SelectedServiceNavigationDirection, HTMLElement | null>
  >({ above: null, below: null });
  const selectedServiceNavigationFallbackTimeoutRef = useRef<number | null>(
    null,
  );
  const handledInitialFocusTargetRef = useRef<string | null>(null);
  const handledSelectedServiceRevealRef = useRef(false);
  const handledInitialConfigurationTargetRef = useRef<string | null>(null);
  const initialConfigurationLauncherRef = useRef<
    (
      input: BeginServiceConfigurationInput,
    ) => SpecialistInitialConfigurationOutcome
  >(() => "rejected");
  const serviceDetailsReturnFocusRef = useRef<HTMLElement | null>(null);
  const serviceConfigurationReturnFocusRef = useRef<HTMLElement | null>(null);
  const packageDetailsReturnFocusRef = useRef<HTMLElement | null>(null);
  const unavailableServiceIdSet = useMemo(
    () => new Set(unavailableServiceIds),
    [unavailableServiceIds],
  );
  const offeringByServiceId = useMemo(
    () => new Map(offerings.map((offering) => [offering.serviceId, offering])),
    [offerings],
  );
  const serviceEntries = useMemo(
    () =>
      services.flatMap((service) => {
        const offering = offeringByServiceId.get(service.serviceId);
        return offering ? [{ service, offering }] : [];
      }),
    [offeringByServiceId, services],
  );
  const specialistStepOneCapabilityOwner = useMemo(
    () => ({
      locationId: assignment.locationId,
      userLocationId: assignment.userLocationId,
      catalogVersion: workplace.catalogVersion,
    }),
    [
      assignment.locationId,
      assignment.userLocationId,
      workplace.catalogVersion,
    ],
  );
  const catalogDescendantOwnerKey = useMemo(
    () =>
      createFrontendDescendantStateOwnerKey({
        surface: "specialist-services-catalog",
        stage: dataState,
        draftId: null,
        customerAccountId: null,
        frozenSource: {
          catalogSelectionOwnerKey,
          entryPointKind,
          assignment,
          workplace,
          services,
          offerings,
          packages,
          serviceCategoryPublication,
          packageCategoryPublication,
        },
      }),
    [
      assignment,
      catalogSelectionOwnerKey,
      dataState,
      entryPointKind,
      offerings,
      packageCategoryPublication,
      packages,
      serviceCategoryPublication,
      services,
      workplace,
    ],
  );
  useLayoutEffect(() => {
    servicesOwnerIsOpenRef.current = isOpen;
  }, [isOpen]);
  useLayoutEffect(() => {
    activeServiceDetailsRef.current = activeServiceDetails;
  }, [activeServiceDetails]);
  useLayoutEffect(() => {
    activeServiceConfigurationRef.current = activeServiceConfiguration;
  }, [activeServiceConfiguration]);
  const publishedServiceEntries = useMemo(
    () =>
      serviceEntries.filter(({ offering }) =>
        isSpecialistOfferingPublishedAtWorkplace(
          assignment,
          workplace,
          offering,
          locationResourceOnlyExecutionsV1,
        ),
      ),
    [assignment, serviceEntries, workplace, locationResourceOnlyExecutionsV1],
  );
  const serviceById = useMemo(
    () => new Map(services.map((service) => [service.serviceId, service])),
    [services],
  );
  const bundlePlanByServiceId = useMemo(() => {
    const plans = new Map<string, SpecialistServiceBundleAddOnPlan>();

    serviceEntries.forEach(({ service, offering }) => {
      if (service.kind !== "bundle") {
        return;
      }

      const plan = createSpecialistServiceBundleAddOnPlan({
        parentService: service,
        parentOffering: offering,
        resolveChild: (serviceId) => {
          const childService = serviceById.get(serviceId);
          const childOffering = offeringByServiceId.get(serviceId);

          return childService && childOffering
            ? { service: childService, offering: childOffering }
            : null;
        },
      });

      if (plan) {
        plans.set(service.serviceId, plan);
      }
    });

    return plans;
  }, [offeringByServiceId, serviceById, serviceEntries]);
  const isServiceConfigurationAvailable = useCallback(
    (service: SpecialistService, offering: SpecialistServiceOffering) => {
      return (
        (isLocationEntryPoint ||
          isSpecialistStepOneFeasible({
            branch: "service",
            owner: specialistStepOneCapabilityOwner,
            service,
          })) &&
        isSpecialistServiceConfigurationAvailableAtWorkplace({
          locationResourceOnlyExecutionsV1,
          assignment,
          workplace,
          service,
          offering,
          bundlePlan: bundlePlanByServiceId.get(service.serviceId),
        })
      );
    },
    [
      assignment,
      bundlePlanByServiceId,
      isLocationEntryPoint,
      specialistStepOneCapabilityOwner,
      workplace,
    locationResourceOnlyExecutionsV1,
  ],
  );
  const packageById = useMemo(
    () =>
      new Map(
        packages.map((packageItem) => [packageItem.packageId, packageItem]),
      ),
    [packages],
  );
  const effectiveSelectedPackageIds = useMemo(() => {
    if (selectedPackageIds.length !== 1) {
      return [];
    }

    const packageId = selectedPackageIds[0];
    const item = packageId ? packageById.get(packageId) : null;
    return item && canPurchasePackage(item) ? [packageId] : [];
  }, [canPurchasePackage, packageById, selectedPackageIds]);
  const selectedPackageIdSet = useMemo(
    () => new Set(effectiveSelectedPackageIds),
    [effectiveSelectedPackageIds],
  );
  const selectedPackageEntries = useMemo(
    () =>
      effectiveSelectedPackageIds.flatMap((packageId) => {
        const packageItem = packageById.get(packageId);
        return packageItem && canPurchasePackage(packageItem) ? [packageItem] : [];
      }),
    [canPurchasePackage, effectiveSelectedPackageIds, packageById],
  );
  const activePackageDetailsEntry = useMemo(
    () =>
      activePackageDetails
        ? (packageById.get(activePackageDetails.packageId) ?? null)
        : null,
    [activePackageDetails, packageById],
  );
  const activeServiceDetailsEntry = useMemo(
    () =>
      activeServiceDetails
        ? (serviceEntries.find(
            ({ service }) =>
              service.serviceId === activeServiceDetails.serviceId,
          ) ?? null)
        : null,
    [activeServiceDetails, serviceEntries],
  );
  const activeServiceConfigurationEntry = useMemo(
    () =>
      activeServiceConfiguration
        ? (serviceEntries.find(
            ({ service }) =>
              service.serviceId === activeServiceConfiguration.serviceId,
          ) ?? null)
        : null,
    [activeServiceConfiguration, serviceEntries],
  );
  const activeBundleConfigurationPlan =
    activeServiceConfiguration?.kind === "bundle"
      ? (bundlePlanByServiceId.get(activeServiceConfiguration.serviceId) ??
        null)
      : null;
  const activeConfigurationOffering = activeServiceConfigurationEntry?.offering;
  const activeOrdinaryConfigurationCanRejoin = Boolean(
    activeServiceConfiguration?.kind === "service" &&
    activeServiceConfigurationEntry?.service.kind !== "bundle" &&
    activeConfigurationOffering &&
    (activeServiceConfiguration.initialStep === "variants"
      ? specialistOfferingRequiresVariantSelection(activeConfigurationOffering)
      : specialistOfferingRequiresAddOnConfiguration(
          activeConfigurationOffering,
        ) &&
        (!specialistOfferingRequiresVariantSelection(
          activeConfigurationOffering,
        ) ||
          Boolean(
            getBookableVariant(
              activeConfigurationOffering,
              activeServiceConfiguration.initialVariantId,
            ),
          ))),
  );
  const activeServiceConfigurationCanRejoin = activeServiceConfiguration
    ? Boolean(
        (activeServiceConfiguration.kind === "bundle" &&
          activeServiceConfigurationEntry?.service.kind === "bundle" &&
          activeBundleConfigurationPlan &&
          activeBundleConfigurationPlan.steps.length > 0) ||
        activeOrdinaryConfigurationCanRejoin,
      )
    : true;
  useLayoutEffect(() => {
    const previousOwnerKey = catalogDescendantOwnerKeyRef.current;

    catalogDescendantOwnerKeyRef.current = catalogDescendantOwnerKey;

    if (!previousOwnerKey || previousOwnerKey === catalogDescendantOwnerKey) {
      return undefined;
    }

    const cancelledTransition = pendingServiceSiblingTransitionRef.current;
    const cancelledTransitionReturnTarget =
      cancelledTransition?.sourceKind === "details"
        ? serviceDetailsReturnFocusRef.current
        : cancelledTransition?.sourceKind === "configuration"
          ? serviceConfigurationReturnFocusRef.current
          : null;
    serviceInteractionTokenRef.current += 1;
    const releaseGeneration = serviceInteractionTokenRef.current;
    pendingServiceSiblingTransitionRef.current = null;

    const currentDetails = activeServiceDetailsRef.current;
    const currentConfiguration = activeServiceConfigurationRef.current;
    // An exiting keyed shell, a removed Service/Offering pair, or a bundle
    // whose exact child plan no longer resolves cannot ever publish onClosed
    // under the replacement owner. Release those presenters synchronously at
    // the ref boundary and settle their render state on the next frame.
    const shouldReleaseDetails = Boolean(
      currentDetails && (!currentDetails.isOpen || !activeServiceDetailsEntry),
    );
    const shouldReleaseConfiguration = Boolean(
      currentConfiguration &&
      (!currentConfiguration.isOpen || !activeServiceConfigurationCanRejoin),
    );

    if (shouldReleaseDetails) {
      activeServiceDetailsRef.current = null;
    }

    if (shouldReleaseConfiguration) {
      activeServiceConfigurationRef.current = null;
    }

    const releaseFrame = window.requestAnimationFrame(() => {
      if (serviceInteractionTokenRef.current !== releaseGeneration) {
        return;
      }

      setPendingServiceSiblingTransition(null);

      if (shouldReleaseDetails) {
        setActiveServiceDetails(null);
      }

      if (shouldReleaseConfiguration) {
        setActiveServiceConfiguration(null);
      }

      if (
        cancelledTransitionReturnTarget?.isConnected &&
        !cancelledTransitionReturnTarget.matches(":disabled") &&
        !cancelledTransitionReturnTarget.closest(
          '[inert], [aria-hidden="true"]',
        )
      ) {
        cancelledTransitionReturnTarget.focus({ preventScroll: true });
      }
    });

    return () => window.cancelAnimationFrame(releaseFrame);
  }, [
    activeServiceConfigurationCanRejoin,
    activeServiceDetailsEntry,
    catalogDescendantOwnerKey,
  ]);
  const requestedSelectedServices = useMemo<
    readonly SpecialistServiceSelection[]
  >(
    () =>
      selectedServices ??
      selectedServiceIds.map((serviceId) => ({
        serviceId,
        variantId: null,
      })),
    [selectedServiceIds, selectedServices],
  );
  const resolveRequestedServiceConfiguration = useCallback(
    (selection: SpecialistServiceSelection) => {
      const service = serviceById.get(selection.serviceId);
      const offering = offeringByServiceId.get(selection.serviceId);

      if (!service || !offering) {
        return null;
      }

      if (!isServiceConfigurationAvailable(service, offering)) {
        return null;
      }

      if (service.kind === "bundle") {
        const plan = bundlePlanByServiceId.get(selection.serviceId);

        if (!plan) {
          return null;
        }

        const configuration = resolveSpecialistServiceBundleConfiguration(
          plan,
          {
            ...selection,
            variantId: null,
          },
        );

        return configuration?.canConfirm ? configuration : null;
      }

      return resolveSpecialistServiceConfiguration(offering, selection);
    },
    [
      bundlePlanByServiceId,
      isServiceConfigurationAvailable,
      offeringByServiceId,
      serviceById,
    ],
  );
  const canonicalRequestedServices = useMemo(() => {
    const normalized: SpecialistServiceSelection[] = [];
    const seenServiceIds = new Set<string>();

    requestedSelectedServices.forEach((selection) => {
      if (
        seenServiceIds.has(selection.serviceId) ||
        unavailableServiceIdSet.has(selection.serviceId)
      ) {
        return;
      }

      const offering = offeringByServiceId.get(selection.serviceId);
      const service = serviceById.get(selection.serviceId);

      if (!offering || !service) {
        return;
      }

      if (service.kind === "bundle") {
        const configuration = resolveRequestedServiceConfiguration(selection);

        if (configuration) {
          normalized.push(configuration.selection);
        }
      } else if (specialistOfferingRequiresVariantSelection(offering)) {
        const variant = getBookableVariant(offering, selection.variantId);

        if (variant) {
          const configuration = resolveRequestedServiceConfiguration(selection);

          if (configuration) {
            normalized.push(configuration.selection);
          }
        } else if (
          selection.variantId &&
          isSpecialistOfferingPublishedAtWorkplace(
            assignment,
            workplace,
            offering,
          locationResourceOnlyExecutionsV1,
          )
        ) {
          // Retain an explicit stale child long enough for Details to explain
          // and replace it. It remains excluded from effective totals/actions.
          normalized.push({ ...selection });
        }
      } else if (offering.price !== null) {
        const configuration = resolveRequestedServiceConfiguration(selection);

        if (configuration) {
          normalized.push(configuration.selection);
        }
      }

      seenServiceIds.add(selection.serviceId);
    });

    return normalized;
  }, [
    offeringByServiceId,
    assignment,
    requestedSelectedServices,
    resolveRequestedServiceConfiguration,
    serviceById,
    unavailableServiceIdSet,
    workplace,
    locationResourceOnlyExecutionsV1,
  ]);
  const effectiveSelectedServices = useMemo(() => {
    const normalized: SpecialistServiceSelection[] = [];
    const seenServiceIds = new Set<string>();

    requestedSelectedServices.forEach((selection) => {
      if (
        seenServiceIds.has(selection.serviceId) ||
        unavailableServiceIdSet.has(selection.serviceId)
      ) {
        return;
      }

      const configuration = resolveRequestedServiceConfiguration(selection);

      if (configuration) {
        normalized.push(configuration.selection);
      }

      seenServiceIds.add(selection.serviceId);
    });

    return normalized;
  }, [
    requestedSelectedServices,
    resolveRequestedServiceConfiguration,
    unavailableServiceIdSet,
  ]);
  const effectiveSelectedServiceIds = useMemo(
    () => effectiveSelectedServices.map(({ serviceId }) => serviceId),
    [effectiveSelectedServices],
  );
  const selectedSet = useMemo(
    () => new Set(effectiveSelectedServiceIds),
    [effectiveSelectedServiceIds],
  );
  const selectionByServiceId = useMemo(
    () =>
      new Map(
        effectiveSelectedServices.map((selection) => [
          selection.serviceId,
          selection,
        ]),
      ),
    [effectiveSelectedServices],
  );
  const requestedSelectionByServiceId = useMemo(
    () =>
      new Map(
        canonicalRequestedServices.map((selection) => [
          selection.serviceId,
          selection,
        ]),
      ),
    [canonicalRequestedServices],
  );
  const serviceCategories = useMemo(() => {
    const categories: Array<{
      id: string;
      label: string;
      entries: typeof serviceEntries;
    }> = [];
    const featuredEntries = getFeaturedSpecialistServices(
      workplace,
      publishedServiceEntries,
    );

    if (featuredEntries.length > 0) {
      categories.push({
        id: SPECIALIST_FEATURED_CATEGORY_ID,
        label: tAll(`services.${SPECIALIST_FEATURED_CATEGORY_ID}`),
        entries: featuredEntries,
      });
    }

    SPECIALIST_SERVICE_COLLECTION_ORDER.forEach((collectionId) => {
      const collectionEntries = publishedServiceEntries.filter(({ offering }) =>
        offering.collectionIds?.includes(collectionId),
      );

      if (collectionEntries.length > 0) {
        categories.push({
          id: collectionId,
          label: tAll(`services.${collectionId}`),
          entries: collectionEntries,
        });
      }
    });

    const bundleEntries = publishedServiceEntries.filter(
      ({ service }) => service.kind === "bundle",
    );

    if (bundleEntries.length > 0) {
      categories.push({
        id: BUNDLE_CATEGORY_ID,
        label: tAll(`services.${BUNDLE_CATEGORY_ID}`),
        entries: bundleEntries,
      });
    }

    serviceCategoryPublication.forEach((category) => {
      const label = resolveSpecialistCatalogCategoryDisplayLabel(
        category,
        locale,
      );
      const entries = publishedServiceEntries.filter(
        (entry) => entry.service.categoryId === category.categoryId,
      );

      if (label && entries.length > 0) {
        categories.push({
          id: `provider-service-category-${category.order}`,
          label,
          entries,
        });
      }
    });

    return categories;
  }, [
    locale,
    publishedServiceEntries,
    serviceCategoryPublication,
    tAll,
    workplace,
  ]);
  const firstRenderedServiceCategoryById = useMemo(() => {
    const categoryByServiceId = new Map<string, string>();

    serviceCategories.forEach((category) => {
      category.entries.forEach(({ service }) => {
        if (!categoryByServiceId.has(service.serviceId)) {
          categoryByServiceId.set(service.serviceId, category.id);
        }
      });
    });

    return categoryByServiceId;
  }, [serviceCategories]);
  const packageCategories = useMemo(() => {
    return packageCategoryPublication.flatMap((category) => {
      const label = resolveSpecialistCatalogCategoryDisplayLabel(
        category,
        locale,
      );
      const entries = packages.filter(
        (packageItem) => packageItem.categoryId === category.categoryId,
      );

      return label && entries.length > 0
        ? [
            {
              id: `provider-package-category-${category.order}`,
              label,
              entries,
            },
          ]
        : [];
    });
  }, [locale, packageCategoryPublication, packages]);
  const isPackagesView = catalogView === "packages";
  const catalogCategories = isPackagesView
    ? packageCategories
    : serviceCategories;
  const resolvedActiveCategoryId = catalogCategories.some(
    (category) => category.id === activeCategoryId,
  )
    ? activeCategoryId
    : (catalogCategories[0]?.id ?? null);
  const categoryOptions = useMemo(
    () =>
      catalogCategories.map((category) => ({
        value: category.id,
        label: category.label,
      })),
    [catalogCategories],
  );
  const catalogCategoryIds = useMemo(
    () => catalogCategories.map((category) => category.id),
    [catalogCategories],
  );
  const {
    hasLeadingFade: hasCategoryLeadingFade,
    hasOverflow: hasCategoryOverflow,
    hasTrailingFade: hasCategoryTrailingFade,
    navigationRef: categoryNavigationRef,
    railRef: categoryRailRef,
    scrollItemIntoView: scrollCategoryButtonIntoView,
  } = useHorizontalOverflowRail<HTMLDivElement, HTMLElement>({
    activeItemId: resolvedActiveCategoryId,
    enabled:
      isOpen &&
      dataState === "ready" &&
      Boolean(contentElement) &&
      catalogCategories.length >= 2,
    itemAttribute: "data-service-category-button",
    itemIds: catalogCategoryIds,
    inlinePaddingProperty: "--specialist-category-rail-inline-padding",
    edgeFadeWidth: CATEGORY_EDGE_FADE_WIDTH,
    overflowTolerance: CATEGORY_OVERFLOW_TOLERANCE,
    scrollEndTolerance: CATEGORY_SCROLL_END_TOLERANCE,
  });
  const selectedEntries = useMemo(
    () =>
      serviceEntries.flatMap(({ service, offering }) => {
        const selection = selectionByServiceId.get(service.serviceId);

        if (!selection) {
          return [];
        }

        const variant = selection.variantId
          ? getBookableVariant(offering, selection.variantId)
          : null;
        const isBundle = service.kind === "bundle";
        const configuration = resolveRequestedServiceConfiguration(selection);

        return configuration
          ? [
              {
                service,
                offering,
                selection,
                variant,
                configuration,
                isBundle,
              },
            ]
          : [];
      }),
    [
      resolveRequestedServiceConfiguration,
      selectionByServiceId,
      serviceEntries,
    ],
  );
  // Group catalogs are participant-bound transactions. The route-level
  // coordinator owns participant identity and aggregate presentation; this
  // shared catalog renders only the one participant it was keyed for.
  const summarySelectedEntries = selectedEntries;
  const exactServiceBenefitPresentation =
    serviceBenefitPresentation &&
    serviceBenefitPresentation.providerId === FRONTEND_ACTIVITY_PROVIDER_ID &&
    serviceBenefitPresentation.priceLabel.trim().length > 0 &&
    serviceEntries.some(
      ({ service, offering }) =>
        service.kind === "service" &&
        service.serviceId === serviceBenefitPresentation.serviceId &&
        offering.offeringId === serviceBenefitPresentation.offeringId,
    )
      ? serviceBenefitPresentation
      : null;
  const coveredSelectedEntries = exactServiceBenefitPresentation
    ? summarySelectedEntries.filter(({ service, offering }) =>
        service.serviceId === exactServiceBenefitPresentation.serviceId &&
        offering.offeringId === exactServiceBenefitPresentation.offeringId)
    : [];
  const coveredSelectedEntry = coveredSelectedEntries.length === 1 &&
    isUnconfiguredPackageSessionServiceSelection(coveredSelectedEntries[0]!.selection) &&
    coveredSelectedEntries[0]!.configuration.addOnLines.length === 0
      ? coveredSelectedEntries[0]!
      : null;
  const summaryQuantity = (serviceId: string) => appointmentMode === "group"
    ? serviceBenefitsContext?.selectionQuantities?.find(row => row.serviceId === serviceId)?.quantity ?? 1 : 1;
  const selectedValues = summarySelectedEntries.map(({ service, configuration }) => ({ ...configuration,
    durationMinutes: configuration.durationMinutes * summaryQuantity(service.serviceId),
    durationMaxMinutes: configuration.durationMaxMinutes == null ? null : configuration.durationMaxMinutes * summaryQuantity(service.serviceId),
  }));
  const selectedPrices = isPackagesView
    ? selectedPackageEntries.map((packageItem) => {
        const plan = packageBenefits.snapshot.status === "ready" ? packageBenefits.snapshot.plans.get(packageItem.packageId) : null;
        const amount = plan ? specialistPriceMinorUnitsToAmount(plan.totals.merchandiseAmountMinor, plan.cart.currency) : null;
        return plan && amount !== null ? { type: "fixed" as const, amount, currency: plan.cart.currency } : null;
      })
    : summarySelectedEntries.flatMap((entry) =>
        entry === coveredSelectedEntry
          ? []
          : [{ ...entry.configuration.price, amount: entry.configuration.price.amount * summaryQuantity(entry.service.serviceId) }],
      );
  const aggregateCatalogBenefitsEnabled = (!membershipSessionPresentation && !serviceSessionPresentation || Boolean(serviceBenefitsContext?.resolveCoverage || serviceBenefitsContext?.coverage)) &&
    (appointmentMode !== "group" || serviceBenefitsContext?.otherLines !== undefined);
  const serviceBenefits = useServiceCatalogBenefits({ services, offerings, workplace, locale, selections: effectiveSelectedServices,
    context: serviceBenefitsContext, enabled: aggregateCatalogBenefitsEnabled });
  const conflictingSessionPresentation = Boolean(membershipSessionPresentation && serviceSessionPresentation);
  const sessionPresentation = conflictingSessionPresentation ? null : membershipSessionPresentation ?? serviceSessionPresentation;
  const membershipCart = sessionPresentation && !isPackagesView
    ? sessionPresentation.resolveCart(effectiveSelectedServices)
    : null;
  const aggregateCart = aggregateCatalogBenefitsEnabled && !isPackagesView && effectiveSelectedServices.length > 0
    ? serviceBenefits.resolveCart() : null;
  const aggregateOwnTotalMinor = aggregateCart?.result.lines.filter(line => line.participantId === (serviceBenefitsContext?.participantId ?? 'organizer'))
    .reduce((sum, line) => sum + line.totalAmountMinor, 0);
  const aggregateCurrency = aggregateCart?.result.evidence.source.currency;
  const aggregateOwnTotal = aggregateOwnTotalMinor !== undefined && aggregateCurrency
    ? specialistPriceMinorUnitsToAmount(aggregateOwnTotalMinor, aggregateCurrency) : null;
  const selectedPriceResolution = aggregateCart && aggregateOwnTotal !== null && aggregateCurrency
    ? { amount: aggregateOwnTotal, currency: aggregateCurrency } :
    aggregateCatalogBenefitsEnabled && !isPackagesView && effectiveSelectedServices.length > 0 && serviceBenefits.snapshot.status === 'ready'
    ? null : conflictingSessionPresentation ? null : sessionPresentation && !isPackagesView
    ? membershipCart ? resolveSpecialistSelectionTotal([membershipCart.price]) : null
    : resolveSpecialistSelectionTotal(selectedPrices);
  const selectedTotal = selectedPriceResolution?.amount ?? 0;
  const selectedCurrency = selectedPriceResolution?.currency ?? "RON";
  const hasUnavailableTotal = selectedPriceResolution === null;
  const hasStartingPrice = aggregateCart ? aggregateCart.starting : membershipCart ? membershipCart.price.type === "from" : selectedPrices.some(
    (price) => price?.type === "from",
  );
  // An empty selection totals to the shared free label, mirroring the
  // marketplace booking-summary idiom, instead of a formatted zero amount.
  const hasFreeTotal =
    !hasUnavailableTotal &&
    (aggregateCart ? selectedTotal === 0 : membershipCart ? membershipCart.price.type === "free" :
      selectedPrices.length === 0 ||
      selectedPrices.every((price) => price?.type === "free"));
  const totalLabel = hasUnavailableTotal
    ? t("common.priceUnavailable")
    : hasFreeTotal
      ? t("common.free")
      : formatSpecialistCurrencyAmount(locale, selectedTotal, selectedCurrency);
  const workplaceRating = workplacePresentation?.rating;
  const normalizedWorkplaceRating =
    typeof workplaceRating === "number" &&
    Number.isFinite(workplaceRating) &&
    workplaceRating >= 0 &&
    workplaceRating <= 5
      ? workplaceRating
      : null;
  const formattedWorkplaceRating =
    normalizedWorkplaceRating === null
      ? null
      : new Intl.NumberFormat(locale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(normalizedWorkplaceRating);
  const hasBookableServices = publishedServiceEntries.some(
    ({ service, offering }) => {
      if (unavailableServiceIdSet.has(service.serviceId)) {
        return false;
      }

      return isServiceConfigurationAvailable(service, offering);
    },
  );
  const groupBookingPolicy = getSpecialistGroupBookingPolicy(workplace);
  const alternativeOptions = getRemainingBookingOptions(
    isPackagesView
      ? "packages"
      : appointmentMode === "group"
        ? "group-appointment"
        : "individual-appointment",
    {
      "individual-appointment": !giftPackageCatalog && hasBookableServices && (
        appointmentMode === "group"
          ? Boolean(onAppointmentModeChange)
          : Boolean(onCatalogViewChange)
      ),
      "group-appointment": Boolean(onAppointmentModeChange) &&
        groupBookingAvailable && groupBookingPolicy !== null && hasBookableServices,
      memberships: Boolean(membershipsHref),
      "gift-cards": Boolean(giftCardsHref),
      packages: !giftPackageCatalog && appointmentMode !== "group" &&
        packages.length > 0 && Boolean(onCatalogViewChange),
      products: appointmentMode !== "group" &&
        workplace.offersOnlineProducts && Boolean(productsHref),
    },
  );
  const isReady = dataState === "ready";
  const isInteractionBusy = isContinuing || isSelectionMutationPending;
  const hasNoServices =
    isReady &&
    (isPackagesView
      ? packages.length === 0
      : publishedServiceEntries.length === 0);
  const hasNoBookableServices =
    !isPackagesView &&
    isReady &&
    publishedServiceEntries.length > 0 &&
    !hasBookableServices;
  const selectedCatalogCount = isPackagesView
    ? effectiveSelectedPackageIds.length
    : effectiveSelectedServices.length;
  const canContinue = isPackagesView
    ? isReady &&
      effectiveSelectedPackageIds.length > 0 &&
      !hasUnavailableTotal &&
      !isInteractionBusy
    : isReady &&
      hasBookableServices &&
      effectiveSelectedServices.length > 0 &&
      (serviceBenefitPresentation === null || coveredSelectedEntry !== null) &&
      continuationUnavailableLabel === null &&
      !hasUnavailableTotal &&
      !isInteractionBusy;
  const selectedServiceCountForDismissal = effectiveSelectedServices.length;

  const guardSelectedServiceDismissal = useCallback(
    (reason: SpecialistServicesDismissReason) => {
      if (selectionMutationInFlightRef.current) {
        return false;
      }

      if (reason === "page-back") return true;
      if (reason === "page-close") {
        setPendingDismissReason(reason);
        return false;
      }

      if (!confirmSelectionOnDismiss) {
        return true;
      }

      if (
        selectedServiceCountForDismissal === 0 &&
        effectiveSelectedPackageIds.length === 0
      ) {
        return true;
      }

      setPendingDismissReason(reason);
      return false;
    },
    [
      confirmSelectionOnDismiss,
      effectiveSelectedPackageIds.length,
      selectedServiceCountForDismissal,
    ],
  );

  const runCatalogEdit = useCallback(
    (publish: () => boolean | void | Promise<boolean | void>) => {
      if (selectionMutationInFlightRef.current) {
        return false;
      }

      const ownerKey = catalogDescendantOwnerKeyRef.current;

      if (!ownerKey) {
        return false;
      }

      selectionMutationInFlightRef.current = true;
      setIsSelectionMutationPending(true);

      const settle = (accepted: boolean | void) =>
        accepted !== false &&
        ownerKey === catalogDescendantOwnerKeyRef.current &&
        servicesOwnerIsOpenRef.current;
      const release = () => {
        selectionMutationInFlightRef.current = false;
        setIsSelectionMutationPending(false);
      };

      try {
        const publication = publish();

        if (publication instanceof Promise) {
          return publication
            .then(settle)
            .catch(() => false)
            .finally(release);
        }

        const accepted = settle(publication);
        release();
        return accepted;
      } catch {
        release();
        return false;
      }
    },
    [],
  );

  const publishSelectedServices = useCallback(
    async (next: readonly SpecialistServiceSelection[]) => {
      const committed = next.map<SpecialistServiceSelection>((selection) => ({
        serviceId: selection.serviceId,
        variantId: selection.variantId,
        ...(selection.addOns
          ? {
              addOns: selection.addOns.map((addOn) => ({ ...addOn })),
            }
          : {}),
        ...(selection.bundleItems
          ? {
              bundleItems: selection.bundleItems.map((bundleItem) => ({
                ...bundleItem,
                addOns: bundleItem.addOns.map((addOn) => ({ ...addOn })),
              })),
            }
          : {}),
      }));

      const transaction = runCatalogEdit(() =>
        onSelectedServicesChange?.(committed),
      );
      const accepted =
        transaction instanceof Promise ? await transaction : transaction;

      if (!accepted) {
        return false;
      }

      onSelectedServiceIdsChange?.(committed.map(({ serviceId }) => serviceId));
      pendingSelectedServicesRef.current = committed;
      return true;
    },
    [onSelectedServiceIdsChange, onSelectedServicesChange, runCatalogEdit],
  );

  const publishSelectedPackages = useCallback(
    (next: readonly string[]) =>
      runCatalogEdit(() => onSelectedPackageIdsChange?.([...next])),
    [onSelectedPackageIdsChange, runCatalogEdit],
  );

  const cancelSelectedServiceDismissal = useCallback(() => {
    setPendingDismissReason(null);
  }, []);

  const confirmSelectedServiceDismissal = useCallback(() => {
    if (!pendingDismissReason) {
      return;
    }

    const reason = pendingDismissReason;
    setPendingDismissReason(null);

    if (reason === "page-back" || reason === "page-close") {
      onPageExit?.(reason === "page-back" ? "back" : "close");
      return;
    }

    if (reason === "workplace-navigation") {
      router.push(workplaceHref);
      return;
    }

    onDismiss?.(reason);
    onClose();
  }, [
    onClose,
    onDismiss,
    onPageExit,
    pendingDismissReason,
    router,
    workplaceHref,
  ]);

  const requestPageExit = useCallback(
    (action: SpecialistServicesPageExitAction) => {
      const dismissReason: SpecialistServicesDismissReason =
        action === "back" ? "page-back" : "page-close";

      if (guardSelectedServiceDismissal(dismissReason)) {
        onPageExit?.(action);
      }
    },
    [guardSelectedServiceDismissal, onPageExit],
  );

  const requestWorkplaceNavigation = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (guardSelectedServiceDismissal("workplace-navigation")) {
        router.push(workplaceHref);
      }
    },
    [guardSelectedServiceDismissal, router, workplaceHref],
  );

  const openServiceDetails = (
    serviceId: string,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    serviceDetailsReturnFocusRef.current = event.currentTarget;
    const interactionToken = serviceInteractionTokenRef.current + 1;
    serviceInteractionTokenRef.current = interactionToken;
    const target: ActiveServiceDetailsState = {
      interactionToken,
      serviceId,
      isOpen: true,
    };
    const currentDetails = activeServiceDetailsRef.current;
    const currentConfiguration = activeServiceConfigurationRef.current;
    const source = currentConfiguration
      ? { kind: "configuration" as const, value: currentConfiguration }
      : currentDetails
        ? { kind: "details" as const, value: currentDetails }
        : null;

    if (source) {
      publishPendingServiceSiblingTransition({
        catalogOwnerKey: catalogDescendantOwnerKey,
        fromInteractionToken: source.value.interactionToken,
        kind: "details",
        sourceKind: source.kind,
        target,
        transitionToken: interactionToken,
      });

      if (source.kind === "configuration") {
        const closing = { ...source.value, isOpen: false };
        activeServiceConfigurationRef.current = closing;
        setActiveServiceConfiguration(closing);
      } else {
        const closing = { ...source.value, isOpen: false };
        activeServiceDetailsRef.current = closing;
        setActiveServiceDetails(closing);
      }
      return;
    }

    publishPendingServiceSiblingTransition(null);
    activeServiceDetailsRef.current = target;
    setActiveServiceDetails(target);
  };

  const closeServiceDetails = () => {
    serviceInteractionTokenRef.current += 1;
    publishPendingServiceSiblingTransition(null);
    const current = activeServiceDetailsRef.current;

    if (!current) {
      return;
    }

    const closing = { ...current, isOpen: false };
    activeServiceDetailsRef.current = closing;
    setActiveServiceDetails(closing);
  };

  const presentPendingServiceSibling = (
    sourceKind: PendingServiceSiblingTransition["sourceKind"],
    interactionToken: number,
  ) => {
    const pending = pendingServiceSiblingTransitionRef.current;

    if (sourceKind === "details") {
      const current = activeServiceDetailsRef.current;

      if (!current || current.interactionToken !== interactionToken) {
        return;
      }

      activeServiceDetailsRef.current = null;
      setActiveServiceDetails(null);
    } else {
      const current = activeServiceConfigurationRef.current;

      if (!current || current.interactionToken !== interactionToken) {
        return;
      }

      activeServiceConfigurationRef.current = null;
      setActiveServiceConfiguration(null);
    }

    if (
      !pending ||
      pending.sourceKind !== sourceKind ||
      pending.fromInteractionToken !== interactionToken ||
      pending.transitionToken !== serviceInteractionTokenRef.current ||
      pending.catalogOwnerKey !== catalogDescendantOwnerKeyRef.current ||
      !servicesOwnerIsOpenRef.current
    ) {
      return;
    }

    publishPendingServiceSiblingTransition(null);

    if (pending.kind === "configuration") {
      activeServiceConfigurationRef.current = pending.target;
      setActiveServiceConfiguration(pending.target);
    } else {
      activeServiceDetailsRef.current = pending.target;
      setActiveServiceDetails(pending.target);
    }
  };

  const openServiceConfiguration = (
    serviceId: string,
    kind: ActiveServiceConfigurationState["kind"],
    initialStep: ActiveServiceConfigurationState["initialStep"],
    initialVariantId: string | null,
    returnToDetails: boolean,
    event?: MouseEvent<HTMLButtonElement>,
    initialBundleItems?: readonly SpecialistServiceBundleItemSelection[],
    initialAddOns?: readonly SpecialistServiceAddOnSelection[],
    returnFocusTarget?: HTMLElement | null,
  ) => {
    if (event) {
      serviceConfigurationReturnFocusRef.current = event.currentTarget;
    } else if (returnFocusTarget) {
      serviceConfigurationReturnFocusRef.current = returnFocusTarget;
    } else {
      serviceConfigurationReturnFocusRef.current =
        serviceDetailsReturnFocusRef.current;
    }

    const interactionToken = serviceInteractionTokenRef.current + 1;
    serviceInteractionTokenRef.current = interactionToken;
    const target: ActiveServiceConfigurationState = {
      interactionToken,
      kind,
      initialStep,
      initialVariantId,
      initialAddOns,
      initialBundleItems,
      returnToDetails,
      serviceId,
      isOpen: true,
    };
    const currentDetails = activeServiceDetailsRef.current;
    const currentConfiguration = activeServiceConfigurationRef.current;
    const source = currentDetails
      ? { kind: "details" as const, value: currentDetails }
      : currentConfiguration
        ? { kind: "configuration" as const, value: currentConfiguration }
        : null;

    if (source) {
      publishPendingServiceSiblingTransition({
        catalogOwnerKey: catalogDescendantOwnerKey,
        fromInteractionToken: source.value.interactionToken,
        kind: "configuration",
        sourceKind: source.kind,
        target,
        transitionToken: interactionToken,
      });

      if (source.kind === "details") {
        const closing = { ...source.value, isOpen: false };
        activeServiceDetailsRef.current = closing;
        setActiveServiceDetails(closing);
      } else {
        const closing = { ...source.value, isOpen: false };
        activeServiceConfigurationRef.current = closing;
        setActiveServiceConfiguration(closing);
      }
      return;
    }

    publishPendingServiceSiblingTransition(null);
    activeServiceConfigurationRef.current = target;
    setActiveServiceConfiguration(target);
  };

  const closeServiceConfiguration = () => {
    serviceInteractionTokenRef.current += 1;
    publishPendingServiceSiblingTransition(null);
    const current = activeServiceConfigurationRef.current;

    if (!current) {
      return;
    }

    const closing = { ...current, isOpen: false };
    activeServiceConfigurationRef.current = closing;
    setActiveServiceConfiguration(closing);
  };

  const cancelServiceSiblingTransitionAndClose = useCallback(() => {
    serviceInteractionTokenRef.current += 1;
    publishPendingServiceSiblingTransition(null);

    const currentDetails = activeServiceDetailsRef.current;

    if (currentDetails) {
      const closingDetails = { ...currentDetails, isOpen: false };
      activeServiceDetailsRef.current = closingDetails;
      setActiveServiceDetails(closingDetails);
    }

    const currentConfiguration = activeServiceConfigurationRef.current;

    if (currentConfiguration) {
      const closingConfiguration = {
        ...currentConfiguration,
        isOpen: false,
      };
      activeServiceConfigurationRef.current = closingConfiguration;
      setActiveServiceConfiguration(closingConfiguration);
    }
  }, [publishPendingServiceSiblingTransition]);

  const openPackageDetails = (
    packageId: string,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    packageDetailsReturnFocusRef.current = event.currentTarget;
    setActivePackageDetails({ packageId, isOpen: true });
  };

  const closePackageDetails = () => {
    setActivePackageDetails((current) =>
      current ? { ...current, isOpen: false } : current,
    );
  };

  useEffect(() => {
    if (isOpen) {
      return undefined;
    }

    servicesOwnerIsOpenRef.current = false;
    serviceInteractionTokenRef.current += 1;
    pendingServiceSiblingTransitionRef.current = null;
    const currentDetails = activeServiceDetailsRef.current;
    const closingDetails = currentDetails
      ? { ...currentDetails, isOpen: false }
      : null;
    const currentConfiguration = activeServiceConfigurationRef.current;
    const closingConfiguration = currentConfiguration
      ? { ...currentConfiguration, isOpen: false }
      : null;
    activeServiceDetailsRef.current = closingDetails;
    activeServiceConfigurationRef.current = closingConfiguration;

    const resetFrame = window.requestAnimationFrame(() => {
      setPendingServiceSiblingTransition(null);
      setActiveServiceDetails(closingDetails);
      setActiveServiceConfiguration(closingConfiguration);
      setActivePackageDetails((current) =>
        current ? { ...current, isOpen: false } : current,
      );
      setPendingPackageUpsellId(null);
    });

    return () => window.cancelAnimationFrame(resetFrame);
  }, [isOpen]);

  useEffect(() => {
    pendingSelectedServicesRef.current = effectiveSelectedServices;
  }, [effectiveSelectedServices]);

  useLayoutEffect(() => {
    if (presentation !== "page" || !isOpen || !contentElement) {
      return undefined;
    }

    const scrollBody = contentElement.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const pageHeader = pageHeaderRef.current;
    // The shell-owned page title is the condensed-header sentinel: the
    // centered specialist name appears once the title scrolls behind the
    // responsive header.
    const pageTitle = document.getElementById(pageTitleId);

    if (!scrollBody || !pageHeader || !pageTitle) {
      return undefined;
    }

    let animationFrame: number | null = null;
    const updateCondensedHeader = () => {
      animationFrame = null;
      // Width-agnostic geometry fact; compact/bridge reveal the centered
      // specialist name while expanded transmutes the page title into the
      // header, each through width-scoped CSS.
      const nextCondensed =
        pageTitle.getBoundingClientRect().bottom <=
        pageHeader.getBoundingClientRect().bottom + 8;
      setIsCondensedHeader((current) =>
        current === nextCondensed ? current : nextCondensed,
      );
    };
    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(updateCondensedHeader);
    };
    const resizeObserver = new ResizeObserver(scheduleUpdate);

    resizeObserver.observe(pageHeader);
    resizeObserver.observe(pageTitle);
    scrollBody.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    updateCondensedHeader();

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver.disconnect();
      scrollBody.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [contentElement, isOpen, pageTitleId, presentation]);

  useEffect(() => {
    const selectionIsCanonical =
      requestedSelectedServices.length === canonicalRequestedServices.length &&
      requestedSelectedServices.every(
        (selection, index) =>
          selectionKey(selection) ===
          selectionKey(canonicalRequestedServices[index]!),
      );

    if (!isOpen || !isReady || selectionIsCanonical) {
      requestedSelectionNormalizationRef.current = null;
      return;
    }

    const requestKey = `${requestedSelectedServices
      .map(selectionKey)
      .join(",")}=>${canonicalRequestedServices.map(selectionKey).join(",")}`;
    if (requestedSelectionNormalizationRef.current === requestKey) {
      return;
    }

    requestedSelectionNormalizationRef.current = requestKey;
    const normalizationFrame = window.requestAnimationFrame(() => {
      void publishSelectedServices(canonicalRequestedServices);
    });

    return () => window.cancelAnimationFrame(normalizationFrame);
  }, [
    canonicalRequestedServices,
    isOpen,
    isReady,
    publishSelectedServices,
    requestedSelectedServices,
  ]);

  const clearPendingCategoryScroll = useCallback(() => {
    pendingCategoryScrollRef.current = null;

    if (pendingCategoryScrollTimeoutRef.current !== null) {
      window.clearTimeout(pendingCategoryScrollTimeoutRef.current);
      pendingCategoryScrollTimeoutRef.current = null;
    }
  }, []);

  const beginCategoryScroll = useCallback(
    (categoryId: string, retainDuringScroll: boolean) => {
      clearPendingCategoryScroll();
      setActiveCategoryId(categoryId);

      if (retainDuringScroll) {
        pendingCategoryScrollRef.current = categoryId;
        pendingCategoryScrollTimeoutRef.current = window.setTimeout(() => {
          pendingCategoryScrollRef.current = null;
          pendingCategoryScrollTimeoutRef.current = null;
        }, PROGRAMMATIC_CATEGORY_SCROLL_TIMEOUT);
      }
    },
    [clearPendingCategoryScroll],
  );

  const retainRevealedCardCategory = useCallback(
    (targetCard: HTMLElement) => {
      const targetSection = targetCard.closest<HTMLElement>(
        "[data-service-category]",
      );
      const targetCategoryId = targetSection?.dataset.serviceCategory;

      // A centered row can be visible before its section heading crosses the
      // sticky activation line. Retain the exact rendered copy's category
      // through the reveal, using the ordinary cancellable scroll transaction.
      if (
        targetCategoryId &&
        categorySectionRefs.current.get(targetCategoryId) === targetSection
      ) {
        beginCategoryScroll(targetCategoryId, true);
      } else {
        clearPendingCategoryScroll();
      }
    },
    [beginCategoryScroll, clearPendingCategoryScroll],
  );

  const getStickyCategoryElement = useCallback(() => {
    return categoryNavigationRef.current ?? categoryRailRef.current;
  }, [categoryNavigationRef, categoryRailRef]);

  useLayoutEffect(() => {
    const resetDockedState = () => {
      setIsCategoryNavigationDocked((current) => (current ? false : current));
    };

    if (
      presentation !== "page" ||
      !isOpen ||
      !isReady ||
      !contentElement ||
      catalogCategories.length < 2
    ) {
      resetDockedState();
      return undefined;
    }

    const scrollBody = contentElement.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const categoryNavigation = categoryNavigationRef.current;

    if (!scrollBody || !categoryNavigation) {
      resetDockedState();
      return undefined;
    }

    let animationFrame: number | null = null;
    const updateDockedState = () => {
      animationFrame = null;
      const navigationRect = categoryNavigation.getBoundingClientRect();
      const scrollBodyRect = scrollBody.getBoundingClientRect();
      const nextDockedState = isServicesCategoryNavigationDocked({
        navigationTop: navigationRect.top,
        position: window.getComputedStyle(categoryNavigation).position,
        scrollBodyTop: scrollBodyRect.top,
      });

      setIsCategoryNavigationDocked((current) =>
        current === nextDockedState ? current : nextDockedState,
      );
    };
    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(updateDockedState);
    };

    // Measure before paint so a route opened at a restored scroll position
    // never flashes the resting separator while the rail is already docked.
    updateDockedState();
    scrollBody.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(contentElement);
    resizeObserver.observe(scrollBody);
    resizeObserver.observe(categoryNavigation);

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      scrollBody.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [
    catalogCategories.length,
    categoryNavigationRef,
    contentElement,
    isOpen,
    isReady,
    presentation,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !isReady ||
      !contentElement ||
      catalogCategories.length < 2
    ) {
      return undefined;
    }

    const categoryRail = categoryRailRef.current;

    if (!categoryRail) {
      return undefined;
    }

    let settleTimeout: number | null = null;
    let hasDirectBrowsing = false;

    const clearSettleTimeout = () => {
      if (settleTimeout !== null) {
        window.clearTimeout(settleTimeout);
        settleTimeout = null;
      }
    };
    const revealCurrentPill = () => {
      clearSettleTimeout();

      if (hasDirectBrowsing) {
        return;
      }

      const focusedButton =
        document.activeElement instanceof HTMLElement &&
        document.activeElement.matches("[data-service-category-button]") &&
        categoryRail.contains(document.activeElement)
          ? document.activeElement
          : null;
      const protectedButton =
        focusedButton ??
        categoryRail.querySelector<HTMLElement>(
          '[data-service-category-button][aria-current="true"]',
        );
      const categoryId = protectedButton?.dataset.serviceCategoryButton;

      if (!protectedButton || !categoryId) {
        return;
      }

      scrollCategoryButtonIntoView(categoryId, "auto");
    };
    const scheduleCurrentReveal = () => {
      if (hasDirectBrowsing) {
        return;
      }

      clearSettleTimeout();
      settleTimeout = window.setTimeout(
        revealCurrentPill,
        CATEGORY_RAIL_SETTLE_DELAY,
      );
    };
    const finishDirectBrowsing = () => {
      clearSettleTimeout();
      settleTimeout = window.setTimeout(() => {
        settleTimeout = null;
        hasDirectBrowsing = false;
      }, CATEGORY_RAIL_SETTLE_DELAY);
    };
    const startDirectBrowsing = () => {
      hasDirectBrowsing = true;
      clearSettleTimeout();
    };
    const settleDirectBrowsing = () => {
      if (!hasDirectBrowsing) {
        return;
      }

      finishDirectBrowsing();
    };
    const handleScroll = () => {
      if (hasDirectBrowsing) {
        finishDirectBrowsing();
        return;
      }

      scheduleCurrentReveal();
    };
    const handleScrollEnd = () => {
      if (hasDirectBrowsing) {
        clearSettleTimeout();
        hasDirectBrowsing = false;
        return;
      }

      revealCurrentPill();
    };

    categoryRail.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    categoryRail.addEventListener("scrollend", handleScrollEnd);
    categoryRail.addEventListener("pointerdown", startDirectBrowsing);
    categoryRail.addEventListener("wheel", startDirectBrowsing, {
      passive: true,
    });
    window.addEventListener("pointerup", settleDirectBrowsing);
    window.addEventListener("pointercancel", settleDirectBrowsing);
    categoryRail.addEventListener("touchstart", startDirectBrowsing, {
      passive: true,
    });
    window.addEventListener("touchend", settleDirectBrowsing, {
      passive: true,
    });
    window.addEventListener("touchcancel", settleDirectBrowsing, {
      passive: true,
    });

    return () => {
      clearSettleTimeout();
      categoryRail.removeEventListener("scroll", handleScroll);
      categoryRail.removeEventListener("scrollend", handleScrollEnd);
      categoryRail.removeEventListener("pointerdown", startDirectBrowsing);
      categoryRail.removeEventListener("wheel", startDirectBrowsing);
      window.removeEventListener("pointerup", settleDirectBrowsing);
      window.removeEventListener("pointercancel", settleDirectBrowsing);
      categoryRail.removeEventListener("touchstart", startDirectBrowsing);
      window.removeEventListener("touchend", settleDirectBrowsing);
      window.removeEventListener("touchcancel", settleDirectBrowsing);
    };
  }, [
    catalogCategories.length,
    categoryRailRef,
    contentElement,
    isOpen,
    isReady,
    scrollCategoryButtonIntoView,
  ]);

  useEffect(() => {
    if (!isOpen || !isReady || catalogCategories.length === 0) {
      clearPendingCategoryScroll();
      return undefined;
    }

    const scrollBody = contentElement?.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const categoryNavigation = categoryNavigationRef.current;
    const categoryRail = categoryRailRef.current;

    if (
      !scrollBody ||
      !categoryNavigation ||
      !categoryRail ||
      catalogCategories.length < 2
    ) {
      return undefined;
    }

    let animationFrame: number | null = null;

    const updateActiveCategory = () => {
      animationFrame = null;
      const orderedSections = catalogCategories.flatMap((category) => {
        const element = categorySectionRefs.current.get(category.id);
        return element ? [{ id: category.id, element }] : [];
      });

      if (orderedSections.length === 0) {
        return;
      }

      const stickyCategoryElement = getStickyCategoryElement();

      if (!stickyCategoryElement) {
        return;
      }

      const stickyCategoryRect = stickyCategoryElement.getBoundingClientRect();
      const activationLine =
        stickyCategoryRect.bottom + CATEGORY_ACTIVATION_GAP;
      const maximumScrollTop = Math.max(
        scrollBody.scrollHeight - scrollBody.clientHeight,
        0,
      );
      const isAtScrollableEnd =
        maximumScrollTop > CATEGORY_SCROLL_END_TOLERANCE &&
        maximumScrollTop - scrollBody.scrollTop <=
          CATEGORY_SCROLL_END_TOLERANCE;
      const pendingCategoryId = pendingCategoryScrollRef.current;

      if (pendingCategoryId) {
        const pendingSection =
          categorySectionRefs.current.get(pendingCategoryId);
        const pendingIsLast = orderedSections.at(-1)?.id === pendingCategoryId;
        const pendingReachedLine = pendingSection
          ? Math.abs(
              pendingSection.getBoundingClientRect().top - activationLine,
            ) <= CATEGORY_SCROLL_END_TOLERANCE
          : true;

        if (!pendingReachedLine && !(pendingIsLast && isAtScrollableEnd)) {
          setActiveCategoryId((current) =>
            current === pendingCategoryId ? current : pendingCategoryId,
          );
          return;
        }

        clearPendingCategoryScroll();
      }

      let nextCategoryId = orderedSections[0]?.id ?? null;

      if (isAtScrollableEnd) {
        nextCategoryId = orderedSections.at(-1)?.id ?? nextCategoryId;
      } else {
        orderedSections.forEach(({ id, element }) => {
          if (element.getBoundingClientRect().top <= activationLine) {
            nextCategoryId = id;
          }
        });
      }

      setActiveCategoryId((current) =>
        current === nextCategoryId ? current : nextCategoryId,
      );
    };

    const scheduleUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(updateActiveCategory);
    };
    const cancelProgrammaticScroll = () => {
      clearPendingCategoryScroll();
      scheduleUpdate();
    };

    scheduleUpdate();
    scrollBody.addEventListener("scroll", scheduleUpdate, { passive: true });
    scrollBody.addEventListener("wheel", cancelProgrammaticScroll, {
      passive: true,
    });
    scrollBody.addEventListener("touchstart", cancelProgrammaticScroll, {
      passive: true,
    });
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(scrollBody);
    resizeObserver.observe(categoryNavigation);
    resizeObserver.observe(categoryRail);
    catalogCategories.forEach((category) => {
      const section = categorySectionRefs.current.get(category.id);

      if (section) {
        resizeObserver.observe(section);
      }
    });

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      scrollBody.removeEventListener("scroll", scheduleUpdate);
      scrollBody.removeEventListener("wheel", cancelProgrammaticScroll);
      scrollBody.removeEventListener("touchstart", cancelProgrammaticScroll);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [
    catalogCategories,
    categoryNavigationRef,
    categoryRailRef,
    clearPendingCategoryScroll,
    contentElement,
    getStickyCategoryElement,
    isOpen,
    isReady,
  ]);

  useEffect(
    () => () => clearPendingCategoryScroll(),
    [clearPendingCategoryScroll],
  );

  const revealCatalogAction = useCallback(
    (target: SpecialistCatalogFocusTarget) => {
      const servicesColumn = servicesColumnRef.current;
      const scrollBody = contentElement?.closest<HTMLElement>(
        SELECTOR_SCROLL_BODY_SELECTOR,
      );

      if (!servicesColumn || !scrollBody) {
        return null;
      }

      const targetAction = Array.from(
        servicesColumn.querySelectorAll<HTMLButtonElement>(
          target.catalog === "packages"
            ? "button[data-package-id]"
            : "button[data-service-id]",
        ),
      ).find((button) =>
        target.catalog === "packages"
          ? button.dataset.packageId === target.itemId
          : button.dataset.serviceId === target.itemId,
      );
      const targetCard = targetAction?.closest<HTMLElement>(
        'article[data-variant="selector"]',
      );

      if (!targetAction || !targetCard) {
        return null;
      }

      const scrollBodyRect = scrollBody.getBoundingClientRect();
      const targetRect = targetCard.getBoundingClientRect();
      const categoryNavigationRect =
        categoryNavigationRef.current?.getBoundingClientRect();
      const visibleTop =
        Math.max(
          scrollBodyRect.top,
          categoryNavigationRect?.bottom ?? scrollBodyRect.top,
        ) + CATEGORY_ACTIVATION_GAP;
      const visibleBottom = scrollBodyRect.bottom - CATEGORY_ACTIVATION_GAP;
      const maximumScrollTop = Math.max(
        scrollBody.scrollHeight - scrollBody.clientHeight,
        0,
      );
      const targetCenter = targetRect.top + targetRect.height / 2;
      const visibleCenter = visibleTop + (visibleBottom - visibleTop) / 2;
      const nextScrollTop = Math.min(
        Math.max(scrollBody.scrollTop + targetCenter - visibleCenter, 0),
        maximumScrollTop,
      );
      const shouldReduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      retainRevealedCardCategory(targetCard);
      targetAction.focus({ preventScroll: true });

      if (typeof scrollBody.scrollTo === "function") {
        scrollBody.scrollTo({
          top: nextScrollTop,
          behavior: shouldReduceMotion ? "auto" : "smooth",
        });
      } else {
        scrollBody.scrollTop = nextScrollTop;
      }

      return targetAction;
    },
    [
      categoryNavigationRef,
      contentElement,
      retainRevealedCardCategory,
    ],
  );

  useLayoutEffect(() => {
    if (!isOpen || !isReady || !resolvedActiveCategoryId) {
      return;
    }

    // Active and restored categories settle before paint. User-driven rail
    // gestures remain untouched because this runs only when active state or
    // measured overflow changes.
    scrollCategoryButtonIntoView(resolvedActiveCategoryId, "auto");
  }, [
    isOpen,
    isReady,
    hasCategoryOverflow,
    resolvedActiveCategoryId,
    scrollCategoryButtonIntoView,
  ]);

  useLayoutEffect(() => {
    if (
      !revealSelectedServiceOnMount ||
      handledSelectedServiceRevealRef.current ||
      presentation !== "page" || !isOpen || !isReady ||
      !contentElement || catalogView !== "services" || initialFocusTarget
    ) return;

    // Catalog order is intentional: the first committed selection may appear
    // later in the authored list. Reuse the ordinary reveal/focus transaction
    // and the existing above/below navigator for the remaining selections.
    const selectedIds = new Set(effectiveSelectedServiceIds);
    const firstSelected = serviceCategories.flatMap(category => category.entries)
      .find(({ service }) => selectedIds.has(service.serviceId));
    if (!firstSelected) return;
    let revealFrame: number | null = null;
    // Let the page shell reset its scroll position before starting the reveal.
    const settleFrame = window.requestAnimationFrame(() => {
      revealFrame = window.requestAnimationFrame(() => {
        if (revealCatalogAction({ catalog: "services", itemId: firstSelected.service.serviceId })) {
          handledSelectedServiceRevealRef.current = true;
        }
      });
    });
    return () => {
      window.cancelAnimationFrame(settleFrame);
      if (revealFrame !== null) window.cancelAnimationFrame(revealFrame);
    };
  }, [catalogView, contentElement, effectiveSelectedServiceIds, initialFocusTarget,
    isOpen, isReady, presentation, revealCatalogAction, revealSelectedServiceOnMount,
    serviceCategories]);

  useEffect(() => {
    if (!initialFocusTarget) {
      handledInitialFocusTargetRef.current = null;
    }
  }, [initialFocusTarget]);

  useLayoutEffect(() => {
    if (
      presentation !== "page" ||
      !isOpen ||
      !isReady ||
      !contentElement ||
      !initialFocusTarget ||
      initialFocusTarget.catalog !== catalogView
    ) {
      return;
    }

    const focusKey = `${initialFocusTarget.catalog}:${initialFocusTarget.itemId}`;

    if (handledInitialFocusTargetRef.current === focusKey) {
      return;
    }

    const revealFrame = window.requestAnimationFrame(() => {
      const targetAction = revealCatalogAction(initialFocusTarget);

      if (!targetAction) return;

      // Mark the request before consuming its URL key so duplicate service
      // copies across promotional and authored categories cannot replay the
      // handoff. DOM order deterministically selects the first rendered copy.
      handledInitialFocusTargetRef.current = focusKey;
      onInitialFocusHandled?.();
    });
    return () => window.cancelAnimationFrame(revealFrame);
  }, [
    catalogView,
    contentElement,
    initialFocusTarget,
    isOpen,
    isReady,
    onInitialFocusHandled,
    presentation,
    revealCatalogAction,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !isReady ||
      isInteractionBusy ||
      effectiveSelectedServiceIds.length === 0 ||
      !contentElement
    ) {
      if (selectedServiceNavigationFallbackTimeoutRef.current !== null) {
        window.clearTimeout(
          selectedServiceNavigationFallbackTimeoutRef.current,
        );
        selectedServiceNavigationFallbackTimeoutRef.current = null;
      }
      selectedServiceNavigationTargetsRef.current = {
        above: null,
        below: null,
      };
      const resetFrame = window.requestAnimationFrame(() => {
        setSelectedServiceNavigation(null);
        setIsSelectedServiceNavigationScrolling(false);
      });

      return () => window.cancelAnimationFrame(resetFrame);
    }

    const scrollBody = contentElement.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const bodyFrame = scrollBody?.parentElement;
    const servicesColumn = servicesColumnRef.current;

    if (!scrollBody || !bodyFrame || !servicesColumn) {
      const resetFrame = window.requestAnimationFrame(() => {
        setSelectedServiceNavigation(null);
        setIsSelectedServiceNavigationScrolling(false);
      });

      return () => window.cancelAnimationFrame(resetFrame);
    }

    let measurementFrame: number | null = null;
    let scrollSettleTimeout: number | null = null;

    const measureSelectedServicePositions = () => {
      measurementFrame = null;

      const scrollBodyRect = scrollBody.getBoundingClientRect();
      const bodyFrameRect = bodyFrame.getBoundingClientRect();
      const servicesColumnRect = servicesColumn.getBoundingClientRect();
      const categoryNavigationRect =
        categoryNavigationRef.current?.getBoundingClientRect();
      const stickyBoundary = Math.max(
        scrollBodyRect.top,
        categoryNavigationRect?.bottom ?? scrollBodyRect.top,
      );
      const visibleTop = stickyBoundary + CATEGORY_ACTIVATION_GAP;
      const visibleBottom = scrollBodyRect.bottom - CATEGORY_ACTIVATION_GAP;

      if (
        scrollBodyRect.height <= 0 ||
        bodyFrameRect.height <= 0 ||
        servicesColumnRect.width <= 0 ||
        visibleBottom <= visibleTop
      ) {
        selectedServiceNavigationTargetsRef.current = {
          above: null,
          below: null,
        };
        setSelectedServiceNavigation(null);
        return;
      }

      const selectedCardsByServiceId = new Map<string, HTMLElement[]>();

      servicesColumn
        .querySelectorAll<HTMLElement>(
          'article[data-variant="selector"][data-selected="true"]',
        )
        .forEach((card) => {
          const serviceId =
            card.querySelector<HTMLElement>("[data-service-id]")?.dataset
              .serviceId;

          if (!serviceId) {
            return;
          }

          const cards = selectedCardsByServiceId.get(serviceId) ?? [];
          cards.push(card);
          selectedCardsByServiceId.set(serviceId, cards);
        });

      let aboveCount = 0;
      let belowCount = 0;
      let closestAbove: { card: HTMLElement; distance: number } | null = null;
      let closestBelow: { card: HTMLElement; distance: number } | null = null;

      for (const serviceId of effectiveSelectedServiceIds) {
        const cards = selectedCardsByServiceId.get(serviceId) ?? [];
        let hasVisibleCopy = false;
        let nearestAbove: { card: HTMLElement; distance: number } | null = null;
        let nearestBelow: { card: HTMLElement; distance: number } | null = null;

        for (const card of cards) {
          const cardRect = card.getBoundingClientRect();

          if (cardRect.height <= 0 || cardRect.width <= 0) {
            continue;
          }

          const visibleHeight =
            Math.min(cardRect.bottom, visibleBottom) -
            Math.max(cardRect.top, visibleTop);

          if (visibleHeight > SELECTED_SERVICE_VISIBILITY_TOLERANCE) {
            hasVisibleCopy = true;
            continue;
          }

          if (cardRect.bottom <= visibleTop) {
            const distance = visibleTop - cardRect.bottom;

            if (!nearestAbove || distance < nearestAbove.distance) {
              nearestAbove = { card, distance };
            }
          } else if (cardRect.top >= visibleBottom) {
            const distance = cardRect.top - visibleBottom;

            if (!nearestBelow || distance < nearestBelow.distance) {
              nearestBelow = { card, distance };
            }
          }
        }

        if (hasVisibleCopy) {
          continue;
        }

        // A service with off-screen copies in both directions feeds both
        // navigators, so opposite-direction wayfinding never collapses into
        // the nearest pill alone.
        if (nearestAbove) {
          aboveCount += 1;

          if (!closestAbove || nearestAbove.distance < closestAbove.distance) {
            closestAbove = nearestAbove;
          }
        }

        if (nearestBelow) {
          belowCount += 1;

          if (!closestBelow || nearestBelow.distance < closestBelow.distance) {
            closestBelow = nearestBelow;
          }
        }
      }

      if (aboveCount === 0 && belowCount === 0) {
        selectedServiceNavigationTargetsRef.current = {
          above: null,
          below: null,
        };
        setSelectedServiceNavigation(null);
        return;
      }

      selectedServiceNavigationTargetsRef.current = {
        above: closestAbove?.card ?? null,
        below: closestBelow?.card ?? null,
      };

      const left = Math.max(servicesColumnRect.left - bodyFrameRect.left, 0);
      const availableWidth = Math.max(
        bodyFrameRect.right - servicesColumnRect.left,
        0,
      );
      const width = Math.min(servicesColumnRect.width, availableWidth);
      const nextSnapshot: SelectedServiceNavigationSnapshot = {
        aboveCount,
        aboveTargetId: closestAbove?.card.id || null,
        belowCount,
        belowTargetId: closestBelow?.card.id || null,
        left,
        top: Math.max(
          stickyBoundary -
            bodyFrameRect.top +
            SELECTED_SERVICE_NAVIGATION_EDGE_GAP,
          SELECTED_SERVICE_NAVIGATION_EDGE_GAP,
        ),
        width,
      };

      setSelectedServiceNavigation((current) =>
        isSameSelectedServiceNavigationSnapshot(current, nextSnapshot)
          ? current
          : nextSnapshot,
      );
    };

    const scheduleMeasurement = () => {
      if (measurementFrame !== null) {
        return;
      }

      measurementFrame = window.requestAnimationFrame(
        measureSelectedServicePositions,
      );
    };
    const finishScrolling = () => {
      if (selectedServiceNavigationFallbackTimeoutRef.current !== null) {
        window.clearTimeout(
          selectedServiceNavigationFallbackTimeoutRef.current,
        );
        selectedServiceNavigationFallbackTimeoutRef.current = null;
      }

      if (scrollSettleTimeout !== null) {
        window.clearTimeout(scrollSettleTimeout);
        scrollSettleTimeout = null;
      }

      setIsSelectedServiceNavigationScrolling(false);
      scheduleMeasurement();
    };
    const handleScroll = () => {
      setIsSelectedServiceNavigationScrolling(true);

      if (scrollSettleTimeout !== null) {
        window.clearTimeout(scrollSettleTimeout);
      }

      scrollSettleTimeout = window.setTimeout(
        finishScrolling,
        SELECTED_SERVICE_NAVIGATION_SETTLE_DELAY,
      );
    };
    const resizeObserver = new ResizeObserver(scheduleMeasurement);

    resizeObserver.observe(scrollBody);
    resizeObserver.observe(bodyFrame);
    resizeObserver.observe(servicesColumn);
    if (categoryNavigationRef.current) {
      resizeObserver.observe(categoryNavigationRef.current);
    }
    servicesColumn
      .querySelectorAll<HTMLElement>(
        'article[data-variant="selector"][data-selected="true"]',
      )
      .forEach((card) => resizeObserver.observe(card));

    scrollBody.addEventListener("scroll", handleScroll, { passive: true });
    scrollBody.addEventListener("scrollend", finishScrolling);
    window.addEventListener("resize", scheduleMeasurement);
    window.visualViewport?.addEventListener("resize", scheduleMeasurement);
    scheduleMeasurement();

    return () => {
      if (measurementFrame !== null) {
        window.cancelAnimationFrame(measurementFrame);
      }
      if (scrollSettleTimeout !== null) {
        window.clearTimeout(scrollSettleTimeout);
      }

      resizeObserver.disconnect();
      scrollBody.removeEventListener("scroll", handleScroll);
      scrollBody.removeEventListener("scrollend", finishScrolling);
      window.removeEventListener("resize", scheduleMeasurement);
      window.visualViewport?.removeEventListener("resize", scheduleMeasurement);
    };
  }, [
    catalogCategories,
    categoryNavigationRef,
    contentElement,
    effectiveSelectedServiceIds,
    isInteractionBusy,
    isOpen,
    isReady,
  ]);

  useEffect(
    () => () => {
      if (selectedServiceNavigationFallbackTimeoutRef.current !== null) {
        window.clearTimeout(
          selectedServiceNavigationFallbackTimeoutRef.current,
        );
      }
    },
    [],
  );

  const scrollToSelectedService = useCallback(
    (direction: SelectedServiceNavigationDirection) => {
      const scrollBody = contentElement?.closest<HTMLElement>(
        SELECTOR_SCROLL_BODY_SELECTOR,
      );
      const targetCard = selectedServiceNavigationTargetsRef.current[direction];

      if (!scrollBody || !targetCard?.isConnected) {
        return;
      }

      const scrollBodyRect = scrollBody.getBoundingClientRect();
      const targetRect = targetCard.getBoundingClientRect();
      const categoryNavigationRect =
        categoryNavigationRef.current?.getBoundingClientRect();
      const visibleTop =
        Math.max(
          scrollBodyRect.top,
          categoryNavigationRect?.bottom ?? scrollBodyRect.top,
        ) + CATEGORY_ACTIVATION_GAP;
      const visibleBottom = scrollBodyRect.bottom - CATEGORY_ACTIVATION_GAP;
      const maximumScrollTop = Math.max(
        scrollBody.scrollHeight - scrollBody.clientHeight,
        0,
      );
      const targetCenter = targetRect.top + targetRect.height / 2;
      const visibleCenter = visibleTop + (visibleBottom - visibleTop) / 2;
      const nextScrollTop = Math.min(
        Math.max(scrollBody.scrollTop + targetCenter - visibleCenter, 0),
        maximumScrollTop,
      );
      const shouldReduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const targetAction = targetCard.querySelector<HTMLButtonElement>(
        "button[data-service-id]",
      );

      if (selectedServiceNavigationFallbackTimeoutRef.current !== null) {
        window.clearTimeout(
          selectedServiceNavigationFallbackTimeoutRef.current,
        );
      }

      // Move focus to a stable modal-owned control before the floating button
      // unmounts. `preventScroll` leaves the modal body as the only scroll owner
      // while the selected card travels into view.
      retainRevealedCardCategory(targetCard);
      targetAction?.focus({ preventScroll: true });
      setIsSelectedServiceNavigationScrolling(true);
      scrollBody.scrollTo({
        top: nextScrollTop,
        behavior: shouldReduceMotion ? "auto" : "smooth",
      });

      selectedServiceNavigationFallbackTimeoutRef.current = window.setTimeout(
        () => {
          selectedServiceNavigationFallbackTimeoutRef.current = null;
          setIsSelectedServiceNavigationScrolling(false);
        },
        shouldReduceMotion
          ? SELECTED_SERVICE_NAVIGATION_SETTLE_DELAY
          : SELECTED_SERVICE_NAVIGATION_SCROLL_FALLBACK_DELAY,
      );
    },
    [categoryNavigationRef, contentElement, retainRevealedCardCategory],
  );

  const scrollToCategory = (categoryId: string) => {
    const scrollBody = contentElement?.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const section = categorySectionRefs.current.get(categoryId);
    const categoryRail = categoryRailRef.current;
    const stickyCategoryElement = getStickyCategoryElement();

    if (!scrollBody || !section || !categoryRail || !stickyCategoryElement) {
      return;
    }

    const shouldReduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const scrollBehavior: ScrollBehavior = shouldReduceMotion
      ? "auto"
      : "smooth";
    const scrollBodyRect = scrollBody.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const stickyCategoryHeight =
      stickyCategoryElement.getBoundingClientRect().height;
    const maximumScrollTop = Math.max(
      scrollBody.scrollHeight - scrollBody.clientHeight,
      0,
    );
    const nextScrollTop = Math.min(
      Math.max(
        scrollBody.scrollTop +
          sectionRect.top -
          scrollBodyRect.top -
          stickyCategoryHeight -
          CATEGORY_ACTIVATION_GAP,
        0,
      ),
      maximumScrollTop,
    );

    beginCategoryScroll(categoryId, !shouldReduceMotion);

    if (typeof scrollBody.scrollTo === "function") {
      scrollBody.scrollTo({ top: nextScrollTop, behavior: scrollBehavior });
    } else {
      scrollBody.scrollTop = nextScrollTop;
    }
  };

  const formatDuration = (
    value: Pick<
      SpecialistServiceOffering,
      "durationMinutes" | "durationMaxMinutes"
    >,
  ) =>
    value.durationMaxMinutes
      ? t("common.durationRange", {
          min: value.durationMinutes,
          max: value.durationMaxMinutes,
        })
      : t("common.duration", { count: value.durationMinutes });

  const formatResponsiveDuration = (
    value: Pick<
      SpecialistServiceOffering,
      "durationMinutes" | "durationMaxMinutes"
    >,
  ) =>
    formatResponsiveBookingDurationRange(
      value.durationMinutes,
      value.durationMaxMinutes,
      t,
    );

  const getBundleCountLabel = (service: SpecialistService) => {
    const bundle = getSpecialistServiceBundle(service);

    return bundle
      ? t("common.bundleServiceCount", { count: bundle.serviceCount })
      : undefined;
  };

  const getAudienceLabel = (offering: SpecialistServiceOffering) =>
    formatSpecialistServiceAudienceLabel(offering.bookableAudiences, (key) =>
      t(`serviceAudience.${key}`),
    );

  const getPriceLabelForPrice = (price: SpecialistPrice | null) => {
    const value = formatPrice(
      price,
      locale,
      t("common.priceUnavailable"),
      t("common.free"),
    );

    return price?.type === "from"
      ? t("common.fromPrice", { price: value })
      : value;
  };

  const getPriceLabel = (offering: SpecialistServiceOffering) =>
    getPriceLabelForPrice(offering.price);

  const getPackageValidityLabel = (packageItem: PackageCatalogDefinition) =>
    isPackageCatalogDefinitionV2(packageItem)
      ? t("profile.packageValidity", { duration: t(`packageBenefitDetails.expiration.${packageItem.expiration.duration.unit}`, { count: packageItem.expiration.duration.value }) })
      : t("profile.packageValidityMonths", {
      count: packageItem.validityMonths,
    });

  const getPackageAppointmentCountLabel = (packageItem: PackageCatalogDefinition) =>
    isPackageCatalogDefinitionV2(packageItem)
      ? t("profile.packageBenefitCount", { count: packageItem.benefits.length })
      : t("profile.packageAppointmentCount", {
      count: packageItem.scheduledAppointmentCount,
    });

  const getSalePresentation = (offering: SpecialistServiceOffering, quantity = 1) => {
    const sale = getSpecialistServiceSale(offering);

    if (!sale) {
      return null;
    }

    return {
      originalPriceLabel: getPriceLabelForPrice({ ...sale.compareAtPrice, amount: sale.compareAtPrice.amount * quantity }),
      dealBadgeLabel: tAll("resultsPage.cards.dealsBadge", {
        percent: sale.discountPercent,
      }),
      dealBadgeAriaLabel: t("common.discountLabel", {
        percent: sale.discountPercent,
      }),
      savingsLabel: t("common.serviceSavings", {
        percent: sale.discountPercent,
      }),
    };
  };

  const getPackageSalePresentation = (packageItem: PackageCatalogDefinition) => {
    const sale = getSpecialistPackageSale(packageItem);

    if (!sale) {
      return null;
    }

    return {
      originalPriceLabel: getPriceLabelForPrice(sale.compareAtPrice),
      dealBadgeLabel: tAll("resultsPage.cards.dealsBadge", {
        percent: sale.discountPercent,
      }),
      dealBadgeAriaLabel: t("common.discountLabel", {
        percent: sale.discountPercent,
      }),
      savingsLabel: t("common.serviceSavings", {
        percent: sale.discountPercent,
      }),
    };
  };

  const activePackageDetailsSale = activePackageDetailsEntry
    ? getPackageSalePresentation(activePackageDetailsEntry)
    : null;
  const activeServiceDetailsSelection = activeServiceDetailsEntry
    ? (requestedSelectionByServiceId.get(
        activeServiceDetailsEntry.service.serviceId,
      ) ?? null)
    : null;
  const activeServiceDetailsVariantId =
    activeServiceDetails?.draftVariantId !== undefined
      ? activeServiceDetails.draftVariantId
      : (activeServiceDetailsSelection?.variantId ?? null);
  const activeServiceDetailsHasBenefit = Boolean(
    activeServiceDetailsEntry &&
      exactServiceBenefitPresentation?.serviceId ===
        activeServiceDetailsEntry.service.serviceId &&
      exactServiceBenefitPresentation.offeringId ===
        activeServiceDetailsEntry.offering.offeringId &&
      isUnconfiguredPackageSessionServiceSelection({
        variantId: activeServiceDetailsVariantId,
        addOns: activeServiceDetails?.draftAddOns ?? activeServiceDetailsSelection?.addOns,
        bundleItems: activeServiceDetails?.draftBundleItems ?? activeServiceDetailsSelection?.bundleItems,
      }),
  );
  const membershipSelectionLabel = (selection: SpecialistServiceSelection): string | null => {
    const projection = sessionPresentation?.resolveSelection(selection);
    if (!projection?.covered) {
      const offering = offeringByServiceId.get(selection.serviceId);
      const configuration = offering && resolveSpecialistServiceConfiguration(offering, selection);
      return configuration && workplace.locationId === 'venue-glow-room'
        ? getServiceSessionDemoPriceLabel({
            serviceId: selection.serviceId,
            locale,
            price: configuration.price,
            formatAmount: (amount, currency) => formatSpecialistCurrencyAmount(locale, amount, currency),
          })
        : null;
    }
    return projection.price.amount === 0 ? sessionPresentation!.sessionLabel :
      `${sessionPresentation!.sessionLabel} + ${getPriceLabelForPrice(projection.price)}`;
  };
  const activeMembershipDetailsLabel = activeServiceDetailsEntry ? membershipSelectionLabel({
    serviceId: activeServiceDetailsEntry.service.serviceId,
    variantId: activeServiceDetailsVariantId,
    ...(activeServiceDetails?.draftAddOns ?? activeServiceDetailsSelection?.addOns
      ? { addOns: activeServiceDetails?.draftAddOns ?? activeServiceDetailsSelection?.addOns } : {}),
  }) : null;
  const activeServiceDetailsSale =
    activeServiceDetailsEntry && !activeServiceDetailsHasBenefit && !activeMembershipDetailsLabel
      ? getSalePresentation(activeServiceDetailsEntry.offering)
      : null;
  const activeServiceDetailsUnavailable = activeServiceDetailsEntry
    ? unavailableServiceIdSet.has(
        activeServiceDetailsEntry.service.serviceId,
      ) ||
      !isServiceConfigurationAvailable(
        activeServiceDetailsEntry.service,
        activeServiceDetailsEntry.offering,
      )
    : false;

  const commitServiceConfiguration = async (
    nextSelection: SpecialistServiceSelection,
  ) => {
    if (
      !isReady ||
      isInteractionBusy ||
      selectionMutationInFlightRef.current ||
      unavailableServiceIdSet.has(nextSelection.serviceId)
    ) {
      return false;
    }

    const service = serviceById.get(nextSelection.serviceId);
    const offering = offeringByServiceId.get(nextSelection.serviceId);

    if (
      !service ||
      !offering ||
      !isServiceConfigurationAvailable(service, offering)
    ) {
      return false;
    }

    const isBundle = service.kind === "bundle";
    const bundlePlan = isBundle
      ? (bundlePlanByServiceId.get(service.serviceId) ?? null)
      : null;
    const variant = specialistOfferingRequiresVariantSelection(offering)
      ? getBookableVariant(offering, nextSelection.variantId)
      : null;

    if (specialistOfferingRequiresVariantSelection(offering) && !variant) {
      return false;
    }

    const resolvedConfiguration = isBundle
      ? bundlePlan
        ? resolveSpecialistServiceBundleConfiguration(bundlePlan, {
            ...nextSelection,
            variantId: null,
          })
        : null
      : resolveSpecialistServiceConfiguration(offering, {
          ...nextSelection,
          variantId: variant?.variantId ?? null,
        });

    if (
      !resolvedConfiguration ||
      ("canConfirm" in resolvedConfiguration &&
        !resolvedConfiguration.canConfirm)
    ) {
      return false;
    }

    const pendingSelections = pendingSelectedServicesRef.current;
    const existingIndex = pendingSelections.findIndex(
      (selection) => selection.serviceId === nextSelection.serviceId,
    );
    const next = [...pendingSelections];
    const committedSelection = resolvedConfiguration.selection;

    if (
      existingIndex >= 0 &&
      selectionKey(pendingSelections[existingIndex]!) ===
        selectionKey(committedSelection)
    ) {
      return true;
    }

    if (existingIndex >= 0) {
      next[existingIndex] = committedSelection;
    } else {
      next.push(committedSelection);
    }

    const accepted = await publishSelectedServices(next);

    if (accepted && existingIndex < 0) {
      onServiceSelectionChange?.({
        action: "selected",
        selectedServiceCount: next.length,
        serviceId: nextSelection.serviceId,
      });
    }

    return accepted;
  };

  const removeService = async (serviceId: string) => {
    if (!isReady || isInteractionBusy || selectionMutationInFlightRef.current) {
      return;
    }

    const pendingSelections = pendingSelectedServicesRef.current;

    if (
      !pendingSelections.some((selection) => selection.serviceId === serviceId)
    ) {
      return;
    }

    const next = pendingSelections.filter(
      (selection) => selection.serviceId !== serviceId,
    );
    if (await publishSelectedServices(next)) {
      onServiceSelectionChange?.({
        action: "deselected",
        selectedServiceCount: next.length,
        serviceId,
      });
    }
  };

  const beginServiceConfiguration = ({
    event,
    initialVariantId = null,
    returnFocusTarget,
    returnToDetails = false,
    serviceId,
  }: BeginServiceConfigurationInput): SpecialistInitialConfigurationOutcome => {
    if (
      !isReady ||
      isInteractionBusy ||
      selectionMutationInFlightRef.current ||
      unavailableServiceIdSet.has(serviceId)
    ) {
      return "rejected";
    }

    const service = serviceById.get(serviceId);
    const offering = offeringByServiceId.get(serviceId);

    if (
      !service ||
      !offering ||
      !isServiceConfigurationAvailable(service, offering)
    ) {
      return "rejected";
    }

    if (service.kind === "bundle") {
      const plan = bundlePlanByServiceId.get(serviceId);

      if (!plan) {
        return "rejected";
      }

      if (plan.steps.length > 0) {
        const initialBundleItems =
          returnToDetails &&
          activeServiceDetails?.isOpen === true &&
          activeServiceDetails.serviceId === serviceId
            ? activeServiceDetails.draftBundleItems
            : undefined;

        openServiceConfiguration(
          serviceId,
          "bundle",
          "add-ons",
          null,
          returnToDetails,
          event,
          initialBundleItems,
          undefined,
          returnFocusTarget,
        );
        return "opened";
      }

      void commitServiceConfiguration({
        serviceId,
        variantId: null,
      }).then((committed) => {
        if (committed) {
          closeServiceDetails();
        }
      });

      return "committed";
    }

    const requiresVariant =
      specialistOfferingRequiresVariantSelection(offering);
    const variant = requiresVariant
      ? getBookableVariant(offering, initialVariantId)
      : null;

    if (requiresVariant && !variant) {
      openServiceConfiguration(
        serviceId,
        "service",
        "variants",
        initialVariantId,
        returnToDetails,
        event,
        undefined,
        undefined,
        returnFocusTarget,
      );
      return "opened";
    }

    const resolvedVariantId = variant?.variantId ?? null;

    if (specialistOfferingRequiresAddOnConfiguration(offering)) {
      const initialAddOns =
        returnToDetails &&
        activeServiceDetails?.isOpen === true &&
        activeServiceDetails.serviceId === serviceId &&
        activeServiceDetails.draftAddOns !== undefined
          ? activeServiceDetails.draftAddOns
          : undefined;

      openServiceConfiguration(
        serviceId,
        "service",
        "add-ons",
        resolvedVariantId,
        returnToDetails,
        event,
        undefined,
        initialAddOns,
        returnFocusTarget,
      );
      return "opened";
    }

    void commitServiceConfiguration({
      serviceId,
      variantId: resolvedVariantId,
    }).then((committed) => {
      if (committed) {
        closeServiceDetails();
      }
    });

    return "committed";
  };

  useLayoutEffect(() => {
    initialConfigurationLauncherRef.current = beginServiceConfiguration;
  });

  useEffect(() => {
    if (!initialConfigurationTarget) {
      handledInitialConfigurationTargetRef.current = null;
    }
  }, [initialConfigurationTarget]);

  useLayoutEffect(() => {
    if (
      presentation !== "page" ||
      catalogView !== "services" ||
      !isOpen ||
      !isReady ||
      !contentElement ||
      !initialConfigurationTarget
    ) {
      return;
    }

    const configurationKey = `${initialConfigurationTarget.serviceId}:${
      initialConfigurationTarget.variantId ?? ""
    }`;

    if (handledInitialConfigurationTargetRef.current === configurationKey) {
      return;
    }

    const launchFrame = window.requestAnimationFrame(() => {
      const targetAction = revealCatalogAction({
        catalog: "services",
        itemId: initialConfigurationTarget.serviceId,
      });

      if (!targetAction) return;

      // Consume before opening or direct-committing so Strict Mode, duplicate
      // category copies, and URL rerenders cannot replay the handoff.
      handledInitialConfigurationTargetRef.current = configurationKey;
      const outcome = initialConfigurationLauncherRef.current({
        initialVariantId: initialConfigurationTarget.variantId,
        returnFocusTarget: targetAction,
        serviceId: initialConfigurationTarget.serviceId,
      });
      onInitialConfigurationHandled?.(outcome);
    });

    return () => window.cancelAnimationFrame(launchFrame);
  }, [
    catalogView,
    contentElement,
    initialConfigurationTarget,
    isOpen,
    isReady,
    onInitialConfigurationHandled,
    presentation,
    revealCatalogAction,
  ]);

  const returnToServiceDetails = (
    draftSelection: SpecialistServiceSelection,
  ) => {
    const current = activeServiceConfigurationRef.current;
    const serviceId = current?.serviceId;

    if (
      !serviceId ||
      current.kind !== "service" ||
      !current.returnToDetails ||
      draftSelection.serviceId !== serviceId
    ) {
      return;
    }

    const interactionToken = serviceInteractionTokenRef.current + 1;
    serviceInteractionTokenRef.current = interactionToken;
    const target: ActiveServiceDetailsState = {
      interactionToken,
      serviceId,
      isOpen: true,
      draftVariantId: draftSelection.variantId,
      draftAddOns: (draftSelection.addOns ?? []).map((addOn) => ({
        ...addOn,
      })),
    };
    publishPendingServiceSiblingTransition({
      catalogOwnerKey: catalogDescendantOwnerKey,
      fromInteractionToken: current.interactionToken,
      kind: "details",
      sourceKind: "configuration",
      target,
      transitionToken: interactionToken,
    });
    const closing = { ...current, isOpen: false };
    activeServiceConfigurationRef.current = closing;
    setActiveServiceConfiguration(closing);
  };

  const returnBundleToServiceDetails = (
    draftSelection: SpecialistServiceSelection,
  ) => {
    const current = activeServiceConfigurationRef.current;
    const serviceId = current?.serviceId;

    if (
      !serviceId ||
      current.kind !== "bundle" ||
      !current.returnToDetails ||
      draftSelection.serviceId !== serviceId
    ) {
      return;
    }

    const interactionToken = serviceInteractionTokenRef.current + 1;
    serviceInteractionTokenRef.current = interactionToken;
    const target: ActiveServiceDetailsState = {
      interactionToken,
      serviceId,
      isOpen: true,
      draftVariantId: null,
      draftBundleItems: draftSelection.bundleItems,
    };
    publishPendingServiceSiblingTransition({
      catalogOwnerKey: catalogDescendantOwnerKey,
      fromInteractionToken: current.interactionToken,
      kind: "details",
      sourceKind: "configuration",
      target,
      transitionToken: interactionToken,
    });
    const closing = { ...current, isOpen: false };
    activeServiceConfigurationRef.current = closing;
    setActiveServiceConfiguration(closing);
  };

  const togglePackage = async (packageId: string) => {
    if (
      !isReady ||
      isInteractionBusy ||
      selectionMutationInFlightRef.current ||
      !packageById.has(packageId) ||
      !canPurchasePackage(packageById.get(packageId)!)
    ) {
      return;
    }

    const isSelected = selectedPackageIdSet.has(packageId);
    const next = isSelected ? [] : [packageId];
    await publishSelectedPackages(next);
  };

  const totalValueLabel =
    hasStartingPrice && !hasFreeTotal && !hasUnavailableTotal
      ? t("servicesModal.totalFrom", { total: totalLabel })
      : totalLabel;
  // The Fresha-reference footer meta sums selected durations; range offerings
  // contribute their bounds so the total stays a truthful min–max window.
  const selectedDurationTotals = selectedValues.reduce(
    (totals, value) => ({
      min: totals.min + value.durationMinutes,
      max:
        totals.max + (value.durationMaxMinutes ?? value.durationMinutes),
    }),
    { min: 0, max: 0 },
  );
  const selectedResponsiveDurationLabel =
    selectedDurationTotals.max > selectedDurationTotals.min
      ? formatResponsiveBookingDurationRange(
          selectedDurationTotals.min,
          selectedDurationTotals.max,
          t,
        )
      : formatResponsiveBookingDuration(selectedDurationTotals.min, t);
  const selectedCatalogMetaLabel = isPackagesView
    ? selectedPackageEntries[0]
      ? getPackageAppointmentCountLabel(selectedPackageEntries[0])
      : t("profile.packageAppointmentCount", { count: 0 })
    : selectedResponsiveDurationLabel;
  const bookingSummaryItems: SpecialistBookingSummaryItem[] = isPackagesView
    ? selectedPackageEntries.map((packageItem) => {
        const salePresentation = getPackageSalePresentation(packageItem);

        return {
          id: packageItem.packageId,
          metaLabel: `${getPackageValidityLabel(packageItem)} · ${getPackageAppointmentCountLabel(packageItem)}`,
          name: packageItem.name,
          originalPriceLabel: salePresentation?.originalPriceLabel,
          priceLabel: getPriceLabelForPrice(packageItem.price),
        };
      })
    : summarySelectedEntries.map(
        ({
          service,
          offering,
          variant,
          configuration,
          isBundle,
        }) => {
          const quantity = summaryQuantity(service.serviceId);
          const name = variant && variant.name !== service.name ? `${service.name} — ${variant.name}` : service.name;
          const duration = formatDuration({ ...configuration, durationMinutes: configuration.durationMinutes * quantity,
            durationMaxMinutes: configuration.durationMaxMinutes == null ? null : configuration.durationMaxMinutes * quantity });
          const isCoveredByServiceBenefit = Boolean(
            coveredSelectedEntry?.service.serviceId === service.serviceId &&
              coveredSelectedEntry.offering.offeringId === offering.offeringId,
          );
          const membershipLabel = membershipSelectionLabel(configuration.selection);
          const demoSessionLabel = workplace.locationId === "venue-glow-room" && configuration.price.currency === "RON"
            ? getServiceSessionDemoBadgeLabel({ serviceId: service.serviceId, locale })
            : null;
          const salePresentation =
            isCoveredByServiceBenefit || membershipLabel ||
            variant ||
            configuration.addOnLines.length > 0
              ? null
              : getSalePresentation(offering, quantity);
          const configuredOriginal = !variant && !isBundle ? getServiceCatalogCompareAtAmountMinor({ offering,
            variantId: null, currency: configuration.price.currency,
            grossAmountMinor: (specialistPriceAmountToMinorUnits(configuration.price.amount, configuration.price.currency) ?? 0) * quantity,
            quantity }) : null;
          const supplementaryLabels = configuration.addOnLines.map((line) => {
            const { addOn, addedPriceAmount, quantity: perServiceQuantity } = line;
            const quantity = perServiceQuantity * summaryQuantity(service.serviceId);
            const price = getPriceLabelForPrice({
              ...addOn.price,
              amount: addedPriceAmount * summaryQuantity(service.serviceId),
            });

            if (isBundle && "bundleItem" in line) {
              return {
                id: `${line.bundleItem.itemId}:${line.group.groupId}:${addOn.addOnId}`,
                label:
                  quantity === 1
                    ? t("serviceAddOnsModal.summaryBundleAddOn", {
                        addOn: addOn.name,
                        price,
                        service: line.bundleItem.name,
                      })
                    : t("serviceAddOnsModal.summaryBundleAddOnQuantity", {
                        addOn: addOn.name,
                        price,
                        quantity,
                        service: line.bundleItem.name,
                      }),
              };
            }

            return {
              id: `${line.group.groupId}:${addOn.addOnId}`,
              label:
                quantity === 1
                  ? t("serviceAddOnsModal.summaryAddOn", {
                      addOn: addOn.name,
                      price,
                    })
                  : t("serviceAddOnsModal.summaryAddOnQuantity", {
                      addOn: addOn.name,
                      price,
                      quantity,
                    }),
            };
          });

          const selectedServiceProviderName = initialServiceProfessionalNames?.[service.serviceId] ?? serviceProviderName;
          const showServiceProvider = appointmentMode !== "group" &&
            !locationResourceOnlyExecutionsV1?.some(row => row.offeringId === offering.offeringId);

          return {
            id: service.serviceId,
            metaLabel: showServiceProvider ? t("servicesModal.serviceMeta", {
              duration,
              name: selectedServiceProviderName,
            }) : duration,
            metaLabelPrefix: duration,
            staffLabel: showServiceProvider
              ? tAll("booking.selectSlot.specificSpecialist", { name: selectedServiceProviderName })
              : null,
            name: quantity > 1 ? `${quantity} × ${name}` : name,
            benefitLineIds: Array.from({ length: quantity }, (_, index) => `catalog:${service.serviceId}${index ? `:unit:${index}` : ''}`),
            originalPriceLabel: configuredOriginal !== null
              ? formatSpecialistCurrencyAmount(locale, specialistPriceMinorUnitsToAmount(configuredOriginal, configuration.price.currency)!, configuration.price.currency)
              : salePresentation?.originalPriceLabel,
            isStartingPrice: configuration.price.type === "from",
            coverageLabel: demoSessionLabel ?? undefined,
            hidePrice: Boolean(demoSessionLabel) && configuration.price.amount === 0,
            priceLabel: demoSessionLabel
              ? getPriceLabelForPrice({ ...configuration.price, amount: configuration.price.amount * quantity })
              : membershipLabel ?? (isCoveredByServiceBenefit
              ? exactServiceBenefitPresentation!.priceLabel
              : getPriceLabelForPrice({ ...configuration.price, amount: configuration.price.amount * quantity })),
            supplementaryLabels,
          };
        },
      );
  const groupSummaryParticipantId = groupSummaryContext?.participantId ?? "organizer";
  const currentParticipantSummaryItems = appointmentMode === "group" && groupParticipantName
    ? bookingSummaryItems.map(item => ({ ...item, participant: { id: groupSummaryParticipantId, label: groupParticipantName } }))
    : bookingSummaryItems;
  const otherSummaryParticipants = appointmentMode === "group" ? groupSummaryContext?.others ?? [] : [];
  const participantItemsById = new Map(otherSummaryParticipants.map(participant => [participant.participantId, participant.items]));
  participantItemsById.set(groupSummaryParticipantId, currentParticipantSummaryItems);
  const catalogSummaryItems = appointmentMode === "group" && groupSummaryContext
    ? groupSummaryContext.participantOrder.flatMap(id => participantItemsById.get(id) ?? [])
    : currentParticipantSummaryItems;
  const displayedSummaryItems = aggregateCart ? projectServiceCatalogSummaryBenefits({
    items: catalogSummaryItems, evaluation: aggregateCart.result,
    startingLines: aggregateCart.startingLines,
    participantId: serviceBenefitsContext?.participantId ?? "organizer",
    formatAmount: (minor, currency) => formatSpecialistCurrencyAmount(locale, specialistPriceMinorUnitsToAmount(minor, currency)!, currency),
    fromPrice: price => t("common.fromPrice", { price }),
  }) : catalogSummaryItems;
  const displayedItemCount = appointmentMode === "group" ? displayedSummaryItems.length : selectedCatalogCount;
  const showResponsiveFooter =
    displayedItemCount > 0 &&
    (presentation === "modal" || isResponsiveServicesPage);
  const pendingGuestSummary = appointmentMode === "group" && !isPackagesView && effectiveSelectedServices.length === 0
    ? groupSummaryContext?.pendingGuestSummary : undefined;
  const selectedTotalMinor = specialistPriceAmountToMinorUnits(selectedTotal, selectedCurrency);
  const groupTotalMinor = aggregateCart ? aggregateCart.result.evidence.plan.totals.finalPayableAmountMinor : selectedTotalMinor === null ? null : selectedTotalMinor + otherSummaryParticipants.reduce((sum, participant) => sum + participant.totalAmountMinor, 0);
  const groupTotalAmount = groupTotalMinor !== null && Number.isSafeInteger(groupTotalMinor)
    ? specialistPriceMinorUnitsToAmount(groupTotalMinor, selectedCurrency) : null;
  const displayedTotalValueLabel = pendingGuestSummary ? pendingGuestSummary.totalValueLabel : otherSummaryParticipants.length > 0
    ? hasUnavailableTotal || groupTotalAmount === null || otherSummaryParticipants.some(participant => participant.currency !== selectedCurrency)
      ? t("common.priceUnavailable")
      : groupTotalAmount === 0 ? t("common.free")
      : hasStartingPrice || otherSummaryParticipants.some(participant => participant.hasStartingPrice)
        ? t("servicesModal.totalFrom", { total: formatSpecialistCurrencyAmount(locale, groupTotalAmount, selectedCurrency) })
        : formatSpecialistCurrencyAmount(locale, groupTotalAmount, selectedCurrency)
    : totalValueLabel;
  const displayedDurationMin = Math.max(selectedDurationTotals.min, ...otherSummaryParticipants.map(participant => participant.durationMinutes));
  const displayedDurationMax = Math.max(selectedDurationTotals.max, ...otherSummaryParticipants.map(participant => participant.durationMaxMinutes));
  const displayedCatalogMetaLabel = otherSummaryParticipants.length > 0
    ? displayedDurationMax > displayedDurationMin
      ? formatResponsiveBookingDurationRange(displayedDurationMin, displayedDurationMax, t)
      : formatResponsiveBookingDuration(displayedDurationMin, t)
    : selectedCatalogMetaLabel;
  const bookingSummaryRating =
    workplacePresentation &&
    normalizedWorkplaceRating !== null &&
    formattedWorkplaceRating !== null
      ? {
          accessibleLabel: t("common.ratingWithReviews", {
            rating: formattedWorkplaceRating,
            count: workplacePresentation.reviewCount,
          }),
          reviewCountLabel: t("profile.reviewCountShort", {
            count: workplacePresentation.reviewCount,
          }),
          value: normalizedWorkplaceRating,
          valueLabel: formattedWorkplaceRating,
        }
      : null;
  const loadingState = (
    <div
      className={styles.loadingState}
      role="status"
      aria-label={t(
        isPackagesView
          ? "servicesModal.loadingPackages"
          : "servicesModal.loading",
      )}
    >
      <span className={styles.srOnly}>
        {t(
          isPackagesView
            ? "servicesModal.loadingPackages"
            : "servicesModal.loading",
        )}
      </span>
      {Array.from({ length: 3 }, (_, index) => (
        <SkeletonCard key={index} className={styles.serviceSkeleton}>
          <span>
            <SkeletonTextLine width={index === 1 ? "52%" : "68%"} />
            <SkeletonTextLine height={12} width="34%" />
            <SkeletonTextLine height={12} width="82%" />
            <SkeletonTextLine width="28%" />
          </span>
          <SkeletonButton height={44} width={44} />
        </SkeletonCard>
      ))}
    </div>
  );
  const errorState = (
    <div className={styles.dataState} role="alert">
      <div>
        <h3>{t("servicesModal.errorTitle")}</h3>
        <p>{t("servicesModal.errorDescription")}</p>
      </div>
      {onRetry ? (
        <VoleraButton variant="secondary" onClick={onRetry}>
          {t("servicesModal.retry")}
        </VoleraButton>
      ) : null}
    </div>
  );
  const emptyState = (
    <div className={styles.dataState} role="status">
      <div>
        <h3>
          {t(
            isPackagesView
              ? "servicesModal.emptyPackagesTitle"
              : "servicesModal.emptyTitle",
          )}
        </h3>
        <p>
          {t(
            isPackagesView
              ? "servicesModal.emptyPackagesDescription"
              : "servicesModal.emptyDescription",
          )}
        </p>
      </div>
    </div>
  );
  const hasServicesCatalog = publishedServiceEntries.length > 0;
  const hasPackagesCatalog = packages.length > 0;
  const showCatalogSwitch =
    !giftPackageCatalog && appointmentMode !== "group" && hasServicesCatalog && hasPackagesCatalog;
  const pageTitle = giftPackageCatalog
    ? tAll("location.gifts.packageCatalogTitle")
    : showCatalogSwitch
    ? t("profile.servicesAndPackagesTitle")
    : isPackagesView
      ? t("servicesModal.packagesSelectTitle")
      : t("servicesModal.title");
  const selectedCountLabel = isPackagesView
    ? t("servicesModal.selectedPackageCount", {
        count: selectedCatalogCount,
      })
    : t("servicesModal.selectedCount", { count: displayedItemCount });
  const selectCatalogView = async (nextCatalogView: SpecialistCatalogView) => {
    if (
      isInteractionBusy ||
      selectionMutationInFlightRef.current ||
      nextCatalogView === catalogView ||
      !onCatalogViewChange
    ) {
      return false;
    }

    const accepted = await runCatalogEdit(() =>
      onCatalogViewChange(nextCatalogView),
    );

    if (!accepted) {
      return false;
    }

    setActiveCategoryId(null);
    categorySectionRefs.current.clear();
    pendingCategoryScrollRef.current = null;
    setSelectedServiceNavigation(null);
    cancelServiceSiblingTransitionAndClose();
    setActivePackageDetails((current) =>
      current ? { ...current, isOpen: false } : current,
    );
    return true;
  };
  const openPackageUpsell = async (
    packageId: string,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    if (catalogView === "packages" || !onCatalogViewChange) {
      openPackageDetails(packageId, event);
      return;
    }

    setPendingPackageUpsellId(packageId);
    if (!(await selectCatalogView("packages"))) {
      setPendingPackageUpsellId(null);
    }
  };

  const changeAppointmentMode = async (mode: AppointmentDraftMode) => {
    if (
      isInteractionBusy ||
      selectionMutationInFlightRef.current ||
      mode === appointmentMode ||
      !onAppointmentModeChange
    ) {
      return;
    }

    await runCatalogEdit(() => onAppointmentModeChange(mode));
  };

  useLayoutEffect(() => {
    if (
      !pendingPackageUpsellId ||
      catalogView !== "packages" ||
      !isOpen ||
      !isReady ||
      !contentElement
    ) {
      return;
    }

    if (!packageById.has(pendingPackageUpsellId)) {
      const resetFrame = window.requestAnimationFrame(() => {
        setPendingPackageUpsellId(null);
      });

      return () => window.cancelAnimationFrame(resetFrame);
    }

    const servicesColumn = servicesColumnRef.current;
    const scrollBody = contentElement.closest<HTMLElement>(
      SELECTOR_SCROLL_BODY_SELECTOR,
    );
    const targetCard = Array.from(
      servicesColumn?.querySelectorAll<HTMLElement>(
        "article[data-package-id]",
      ) ?? [],
    ).find((card) => card.dataset.packageId === pendingPackageUpsellId);
    const detailsAction =
      targetCard?.querySelector<HTMLButtonElement>(
        'button[aria-haspopup="dialog"]',
      ) ?? null;

    if (!servicesColumn || !scrollBody || !targetCard || !detailsAction) {
      return;
    }

    const scrollBodyRect = scrollBody.getBoundingClientRect();
    const targetRect = targetCard.getBoundingClientRect();
    const categoryNavigationRect =
      categoryNavigationRef.current?.getBoundingClientRect();
    const visibleTop =
      Math.max(
        scrollBodyRect.top,
        categoryNavigationRect?.bottom ?? scrollBodyRect.top,
      ) + CATEGORY_ACTIVATION_GAP;
    const visibleBottom = scrollBodyRect.bottom - CATEGORY_ACTIVATION_GAP;
    const maximumScrollTop = Math.max(
      scrollBody.scrollHeight - scrollBody.clientHeight,
      0,
    );
    const targetCenter = targetRect.top + targetRect.height / 2;
    const visibleCenter = visibleTop + (visibleBottom - visibleTop) / 2;
    const nextScrollTop = Math.min(
      Math.max(scrollBody.scrollTop + targetCenter - visibleCenter, 0),
      maximumScrollTop,
    );

    clearPendingCategoryScroll();
    detailsAction.focus({ preventScroll: true });

    if (typeof scrollBody.scrollTo === "function") {
      scrollBody.scrollTo({ top: nextScrollTop, behavior: "auto" });
    } else {
      scrollBody.scrollTop = nextScrollTop;
    }

    packageDetailsReturnFocusRef.current = detailsAction;
    const openDetailsFrame = window.requestAnimationFrame(() => {
      setPendingPackageUpsellId(null);
      setActivePackageDetails({
        packageId: pendingPackageUpsellId,
        isOpen: true,
      });
    });

    return () => window.cancelAnimationFrame(openDetailsFrame);
  }, [
    catalogView,
    categoryNavigationRef,
    clearPendingCategoryScroll,
    contentElement,
    isOpen,
    isReady,
    packageById,
    pendingPackageUpsellId,
  ]);
  const continueCatalog = () => {
    if (!canContinue || selectionMutationInFlightRef.current) {
      return;
    }

    if (isPackagesView) {
      onContinuePackages?.(effectiveSelectedPackageIds);
      return;
    }

    onContinue(effectiveSelectedServices);
  };
  const serviceNavigationGeometry =
    selectedServiceNavigation ?? lastServiceNavigationGeometry.current;
  return (
    <>
      {isContinuing ? (
        <span
          className={styles.srOnly}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {t("submissionGate.continuing")}
        </span>
      ) : null}
      <SpecialistServicesPresentationShell
        isOpen={isOpen}
        presentation={presentation}
        onClose={onClose}
        onDismiss={onDismiss}
        onBeforeDismiss={guardSelectedServiceDismissal}
        onPageExit={requestPageExit}
        closeLabel={t(
          showCatalogSwitch
            ? "servicesModal.closeCatalog"
            : isPackagesView
              ? "servicesModal.closePackages"
              : "servicesModal.close",
        )}
        pageBackLabel={
          giftPackageCatalog
            ? tAll("location.gifts.back")
            : isLocationEntryPoint
            ? tAll("location.servicesPage.backToProfile")
            : t("servicesPage.backToProfile")
        }
        pageCloseLabel={
          isLocationEntryPoint
            ? tAll(
                showCatalogSwitch
                  ? "location.servicesPage.closeCatalog"
                  : isPackagesView
                    ? "location.servicesPage.closePackages"
                    : "location.servicesPage.close",
              )
            : t(
                showCatalogSwitch
                  ? "servicesPage.closeCatalog"
                  : isPackagesView
                    ? "servicesPage.closePackages"
                    : "servicesPage.close",
              )
        }
        pageHeaderRef={pageHeaderRef}
        title={pageTitle}
        titleId={pageTitleId}
        isCondensedHeader={isCondensedHeader}
        isDismissDisabled={isInteractionBusy}
        returnFocusRef={returnFocusRef}
        scrollViewportOverlay={
          <div
            className={styles.selectedServicesNavigationOverlay}
            data-scrolling={
              isSelectedServiceNavigationScrolling ? "true" : undefined
            }
            style={
              serviceNavigationGeometry
                ? ({
                  "--selected-services-navigation-left": `${serviceNavigationGeometry.left}px`,
                  "--selected-services-navigation-top": `${serviceNavigationGeometry.top}px`,
                  "--selected-services-navigation-width": `${serviceNavigationGeometry.width}px`,
                } as CSSProperties)
                : undefined
            }
          >
            {(["above", "below"] as const).map((direction) => {
              const count = selectedServiceNavigation?.[
                direction === "above" ? "aboveCount" : "belowCount"
              ] ?? 0;
              const targetId = selectedServiceNavigation?.[
                direction === "above" ? "aboveTargetId" : "belowTargetId"
              ];
              return (
                <SelectedServicesNavigator
                  key={direction}
                  direction={direction}
                  visible={
                    !isPackagesView && !isSelectedServiceNavigationScrolling && count > 0
                  }
                >
                  <VoleraButton
                    variant="secondary"
                    size="md"
                    className={styles.selectedServicesNavigatorButton}
                    aria-controls={targetId ?? undefined}
                    aria-label={t(
                      direction === "above"
                        ? "servicesModal.selectedAboveAction"
                        : "servicesModal.selectedBelowAction",
                      { count },
                    )}
                    data-selected-services-navigator={direction}
                    trailingIcon={
                      <ServiceNavigationArrow
                        className={styles.selectedServicesNavigatorArrow}
                        direction={direction}
                      />
                    }
                    onClick={() => scrollToSelectedService(direction)}
                  >
                    {t("servicesModal.selectedCount", { count })}
                  </VoleraButton>
                </SelectedServicesNavigator>
              );
            })}
          </div>
        }
        footer={
          showResponsiveFooter ? (
            <BookingFlowSelectionFooter
              actionBusy={isInteractionBusy}
              actionDisabled={!canContinue}
              actionLabel={t("servicesModal.continue")}
              durationLabel={displayedCatalogMetaLabel}
              itemCountAriaLabel={selectedCountLabel}
              itemCountLabel={t("servicesModal.footerItems", { count: displayedItemCount })}
              onAction={continueCatalog}
              totalValueLabel={displayedTotalValueLabel}
            />
          ) : undefined
        }
      >
        <div
          ref={setContentElement}
          className={styles.content}
          aria-busy={isInteractionBusy}
        >
          <DataStateBoundary
            analyticsContext={
              presentation === "page"
                ? "specialist-services-page"
                : "specialist-services-modal"
            }
            isEmpty={hasNoServices}
            isError={dataState === "error"}
            isLoaded={isReady}
            isLoading={dataState === "loading"}
            renderEmpty={emptyState}
            renderError={errorState}
            renderLoading={loadingState}
            retry={onRetry}
            retryLabel={t("servicesModal.retry")}
          >
            {hasNoBookableServices ? (
              <div className={styles.dataState} role="status">
                <div>
                  <h3>{t("servicesModal.noBookableTitle")}</h3>
                  <p>{t("servicesModal.noBookableDescription")}</p>
                </div>
              </div>
            ) : null}
            {continuationUnavailableLabel ? <p role="status">{continuationUnavailableLabel}</p> : null}

            <div
              className={styles.desktopComposition}
              data-specialist-services-composition=""
            >
              <div
                ref={servicesColumnRef}
                className={styles.servicesColumn}
                data-specialist-services-column=""
              >
                {appointmentMode === "group" && groupParticipantName ? (
                  <div className={styles.groupParticipant} data-group-services-participant="">
                    <AppointmentGroupParticipantCard name={groupParticipantName} isGuest={groupParticipantIsGuest} />
                  </div>
                ) : null}
                {showCatalogSwitch ? (
                  <SegmentedControl
                    className={styles.catalogSwitch}
                    value={catalogView}
                    ariaLabel={t("profile.catalogSwitchLabel", {
                      name: catalogSubjectName,
                    })}
                    disabled={isInteractionBusy}
                    onChange={(nextCatalogView) =>
                      void selectCatalogView(nextCatalogView)
                    }
                    options={[
                      {
                        value: "services",
                        label: t("profile.servicesTitle"),
                      },
                      {
                        value: "packages",
                        label: t("profile.packagesTitle"),
                      },
                    ]}
                  />
                ) : null}

                {catalogCategories.length > 1 ? (
                  <div
                    ref={categoryNavigationRef}
                    className={styles.categoryNavigation}
                    data-category-docked={
                      isCategoryNavigationDocked ? "" : undefined
                    }
                    data-service-category-navigation=""
                    data-category-leading-fade={
                      hasCategoryOverflow && hasCategoryLeadingFade
                        ? ""
                        : undefined
                    }
                    data-category-trailing-fade={
                      hasCategoryTrailingFade ? "" : undefined
                    }
                  >
                    <span
                      className={styles.categoryNavigationSurface}
                      data-service-category-navigation-surface=""
                      aria-hidden="true"
                    />
                    <nav
                      ref={categoryRailRef}
                      className={styles.categoryRail}
                      aria-label={t(
                        isPackagesView
                          ? "servicesModal.packageCategoryNavigation"
                          : "servicesModal.categoryNavigation",
                      )}
                    >
                      {catalogCategories.map((category) => {
                        const isActive =
                          resolvedActiveCategoryId === category.id;

                        return (
                          <ToolbarPillButton
                            key={category.id}
                            active={isActive}
                            data-service-category-button={category.id}
                            aria-current={isActive ? "true" : undefined}
                            onFocus={() =>
                              scrollCategoryButtonIntoView(category.id, "auto")
                            }
                            onClick={() => scrollToCategory(category.id)}
                          >
                            {category.label}
                          </ToolbarPillButton>
                        );
                      })}
                    </nav>

                    {hasCategoryOverflow && resolvedActiveCategoryId ? (
                      <SelectDropdown
                        value={resolvedActiveCategoryId}
                        options={categoryOptions}
                        onChange={scrollToCategory}
                        searchable={false}
                        placement="below"
                        triggerAriaLabel={t(
                          isPackagesView
                            ? "servicesModal.allPackageCategories"
                            : "servicesModal.allCategories",
                        )}
                        triggerContent={
                          <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle cx="4" cy="6" r="1" />
                            <circle cx="4" cy="12" r="1" />
                            <circle cx="4" cy="18" r="1" />
                            <path d="M9 6h11M9 12h11M9 18h11" />
                          </svg>
                        }
                        popoverAlign="end"
                        popoverPreferredWidth={360}
                        popoverMaxWidth={420}
                        width="44px"
                        responsiveModal
                        responsiveModalTitle={t(
                          isPackagesView
                            ? "servicesModal.allPackageCategories"
                            : "servicesModal.allCategories",
                        )}
                        responsiveModalCloseLabel={tAll("common.close")}
                        focusSelectedOptionOnOpen
                        className={styles.categoryDropdown}
                        buttonClassName={styles.categoryDropdownButton}
                      />
                    ) : null}
                  </div>
                ) : null}

                <div className={styles.serviceList}>
                  {isPackagesView
                    ? packageCategories.map((category) => (
                        <section
                          ref={(element) => {
                            if (element) {
                              categorySectionRefs.current.set(
                                category.id,
                                element,
                              );
                            } else {
                              categorySectionRefs.current.delete(category.id);
                            }
                          }}
                          key={category.id}
                          id={`specialist-category-${category.id}`}
                          className={styles.categorySection}
                          data-service-category={category.id}
                          data-package-category={category.id}
                        >
                          <h3 className={styles.catalogSectionTitle}>{category.label}</h3>
                          <div
                            className={styles.categoryServices}
                            role="list"
                            aria-label={t(
                              "servicesModal.packageCategoryListLabel",
                              {
                                category: category.label,
                                name: catalogSubjectName,
                              },
                            )}
                          >
                            {category.entries.map((packageItem) => {
                              const selected = selectedPackageIdSet.has(
                                packageItem.packageId,
                              );
                              const salePresentation =
                                getPackageSalePresentation(packageItem);

                              return (
                                <SpecialistPackageCard
                                  key={packageItem.packageId}
                                  packageItem={packageItem}
                                  benefitPlan={packageBenefits.snapshot.status === "ready" ? packageBenefits.snapshot.plans.get(packageItem.packageId) ?? null : null}
                                  headingLevel={4}
                                  variant="selector"
                                  selected={selected}
                                  disabled={isInteractionBusy || !canPurchasePackage(packageItem)}
                                  validityLabel={getPackageValidityLabel(
                                    packageItem,
                                  )}
                                  appointmentCountLabel={getPackageAppointmentCountLabel(
                                    packageItem,
                                  )}
                                  includedLabel={t(
                                    "profile.packageIncludedLabel",
                                  )}
                                  responsiveIncludedLabel={t(
                                    "profile.packageIncludedCompactLabel",
                                  )}
                                  priceLabel={getPriceLabelForPrice(
                                    packageItem.price,
                                  )}
                                  originalPriceLabel={
                                    salePresentation?.originalPriceLabel
                                  }
                                  dealBadgeLabel={
                                    salePresentation?.dealBadgeLabel
                                  }
                                  dealBadgeAriaLabel={
                                    salePresentation?.dealBadgeAriaLabel
                                  }
                                  savingsLabel={salePresentation?.savingsLabel}
                                  detailsLabel={t(
                                    "profile.openPackageDetails",
                                    {
                                      package: packageItem.name,
                                    },
                                  )}
                                  actionLabel={t(
                                    selected
                                      ? "packageDetailsModal.removePackage"
                                      : "packageDetailsModal.addPackage",
                                    { package: packageItem.name },
                                  )}
                                  actionText={tAll(
                                    selected ? "common.remove" : "common.add",
                                  )}
                                  onDetails={(event) =>
                                    openPackageDetails(
                                      packageItem.packageId,
                                      event,
                                    )
                                  }
                                  onAction={() =>
                                    void togglePackage(packageItem.packageId)
                                  }
                                />
                              );
                            })}
                          </div>
                        </section>
                      ))
                    : serviceCategories.map((category) => (
                        <section
                          ref={(element) => {
                            if (element) {
                              categorySectionRefs.current.set(
                                category.id,
                                element,
                              );
                            } else {
                              categorySectionRefs.current.delete(category.id);
                            }
                          }}
                          key={category.id}
                          id={`specialist-category-${category.id}`}
                          className={styles.categorySection}
                          data-service-category={category.id}
                        >
                          <h3 className={styles.catalogSectionTitle}>{category.label}</h3>
                          <div
                            className={styles.categoryServices}
                            role="list"
                            aria-label={t("servicesModal.categoryListLabel", {
                              category: category.label,
                              name: catalogSubjectName,
                            })}
                          >
                            {category.entries.map(({ service, offering }) => {
                              const selected = selectedSet.has(
                                service.serviceId,
                              );
                              const selectedVariantId =
                                selectionByServiceId.get(
                                  service.serviceId,
                                )?.variantId;
                              const selectedSelection =
                                selectionByServiceId.get(service.serviceId);
                              const selectedVariant = selectedVariantId
                                ? getBookableVariant(
                                    offering,
                                    selectedVariantId,
                                  )
                                : null;
                              const selectedConfiguration = selectedSelection
                                ? resolveRequestedServiceConfiguration(
                                    selectedSelection,
                                  )
                                : null;
                              const selectedDisplayValue =
                                selectedConfiguration ??
                                selectedVariant ??
                                offering;
                              const unavailable =
                                unavailableServiceIdSet.has(
                                  service.serviceId,
                                ) ||
                                !isServiceConfigurationAvailable(
                                  service,
                                  offering,
                                );
                              const selectionLocked = isInteractionBusy;
                              const isCoveredByServiceBenefit = Boolean(
                                exactServiceBenefitPresentation?.serviceId ===
                                  service.serviceId &&
                                  exactServiceBenefitPresentation.offeringId ===
                                    offering.offeringId &&
                                  isUnconfiguredPackageSessionServiceSelection(
                                    selectedSelection ?? { variantId: null },
                                  ) &&
                                  (selectedConfiguration?.addOnLines.length ?? 0) === 0,
                              );
                              const membershipLabel = membershipSelectionLabel(selectedSelection ?? { serviceId: service.serviceId, variantId: null });
                              const salePresentation =
                                isCoveredByServiceBenefit || membershipLabel ||
                                selectedVariant ||
                                (selectedConfiguration?.addOnLines.length ??
                                  0) > 0
                                  ? null
                                  : getSalePresentation(offering);
                              const bundleCountLabel =
                                getBundleCountLabel(service);
                              const packageUpsell =
                                selected &&
                                appointmentMode !== "group" &&
                                service.kind === "service" &&
                                firstRenderedServiceCategoryById.get(
                                  service.serviceId,
                                ) === category.id
                                  ? getSpecialistPackageUpsellForService(
                                      packages.filter(isPackageCatalogDefinitionV1),
                                      service.serviceId,
                                    )
                                  : null;

                              return (
                                <div key={service.serviceId} role="listitem">
                                  <SpecialistServiceCard
                                    service={service}
                                    benefitPresentation={serviceBenefits.getPresentation(selectedSelection ?? { serviceId: service.serviceId, variantId: null })}
                                    offering={offering}
                                    nameLabel={selectedVariant?.name}
                                    descriptionOverride={
                                      selectedVariant?.description
                                    }
                                    anchorId={`specialist-modal-service-${category.id}-${service.serviceId}`}
                                    headingLevel={4}
                                    durationLabel={formatDuration(
                                      selectedDisplayValue,
                                    )}
                                    responsiveDurationLabel={formatResponsiveDuration(
                                      selectedDisplayValue,
                                    )}
                                    bundleCountLabel={bundleCountLabel}
                                    audienceLabel={getAudienceLabel(offering)}
                                    priceLabel={
                                      membershipLabel ?? (isCoveredByServiceBenefit
                                        ? exactServiceBenefitPresentation!
                                            .priceLabel
                                        : getPriceLabelForPrice(
                                            selectedDisplayValue.price,
                                          ))
                                    }
                                    originalPriceLabel={
                                      salePresentation?.originalPriceLabel
                                    }
                                    dealBadgeLabel={
                                      salePresentation?.dealBadgeLabel
                                    }
                                    dealBadgeAriaLabel={
                                      salePresentation?.dealBadgeAriaLabel
                                    }
                                    savingsLabel={
                                      salePresentation?.savingsLabel
                                    }
                                    selected={selected}
                                    disabled={unavailable || selectionLocked}
                                    disabledReason={
                                      unavailable
                                        ? t("servicesModal.unavailable")
                                        : undefined
                                    }
                                    variant="selector"
                                    actionLabel={
                                      selectionLocked
                                        ? t(
                                            "servicesModal.selectionLockedServiceLabel",
                                            { service: service.name },
                                          )
                                        : unavailable
                                          ? t(
                                              "servicesModal.unavailableServiceLabel",
                                              { service: service.name },
                                            )
                                          : selected
                                            ? t("servicesModal.removeService", {
                                                service: service.name,
                                              })
                                            : t("servicesModal.addService", {
                                                service: service.name,
                                              })
                                    }
                                    detailsLabel={t(
                                      "serviceDetailsModal.open",
                                      { service: service.name },
                                    )}
                                    onDetails={(event) =>
                                      openServiceDetails(
                                        service.serviceId,
                                        event,
                                      )
                                    }
                                    onAction={(event) => {
                                      if (selected) {
                                        void removeService(service.serviceId);
                                        return;
                                      }

                                      beginServiceConfiguration({
                                        event,
                                        serviceId: service.serviceId,
                                      });
                                    }}
                                  />
                                  {packageUpsell ? (
                                    <SpecialistPackageUpsellCard
                                      packageItem={packageUpsell}
                                      sourceServiceId={service.serviceId}
                                      title={t(
                                        "servicesModal.packageUpsellTitle",
                                        {
                                          package: packageUpsell.name,
                                        },
                                      )}
                                      priceMeta={t(
                                        "servicesModal.packageUpsellPrice",
                                        {
                                          price: getPriceLabelForPrice(
                                            packageUpsell.price,
                                          ),
                                        },
                                      )}
                                      actionLabel={t(
                                        "servicesModal.openPackageUpsell",
                                        {
                                          package: packageUpsell.name,
                                          price: getPriceLabelForPrice(
                                            packageUpsell.price,
                                          ),
                                        },
                                      )}
                                      disabled={selectionLocked}
                                      onAction={(event) =>
                                        void openPackageUpsell(
                                          packageUpsell.packageId,
                                          event,
                                        )
                                      }
                                    />
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      ))}

                  {alternativeOptions.length > 0 ? (
                    <section
                      className={styles.alternativeSection}
                      data-specialist-alternatives=""
                    >
                      <h3 className={styles.catalogSectionTitle}>{t("servicesModal.trySomethingElseTitle")}</h3>
                      <div className={styles.alternativeCards}>
                        {alternativeOptions.map((kind) => {
                          const segment = BOOKING_OPTION_COPY[kind];
                          const copy = {
                            kind,
                            legacyDataKind: kind === "group-appointment"
                              ? "group-appointments"
                              : kind,
                            title: tAll(`location.bookingOptions.items.${segment}.title`),
                            description: tAll(`location.bookingOptions.items.${segment}.description`),
                            icon: <BookingOptionIcon kind={kind} />,
                          };
                          if (kind === "individual-appointment" || kind === "group-appointment" || kind === "packages") {
                            return (
                              <BookingOptionCard
                                key={kind}
                                {...copy}
                                actionType="button"
                                disabled={isInteractionBusy}
                                onAction={() => {
                                  if (kind === "packages") {
                                    void selectCatalogView("packages");
                                  } else if (kind === "group-appointment") {
                                    void changeAppointmentMode("group");
                                  } else if (appointmentMode === "group") {
                                    void changeAppointmentMode("individual");
                                  } else {
                                    void selectCatalogView("services");
                                  }
                                }}
                              />
                            );
                          }
                          const href = kind === "memberships"
                            ? membershipsHref
                            : kind === "gift-cards" ? giftCardsHref : productsHref;
                          const onAction = kind === "memberships"
                            ? onMembershipsAction
                            : kind === "gift-cards" ? onGiftCardsAction : onProductsAction;
                          return href ? (
                            <BookingOptionCard
                              key={kind}
                              {...copy}
                              actionType="link"
                              href={href}
                              onAction={onAction}
                            />
                          ) : null;
                        })}
                      </div>
                    </section>
                  ) : null}
                </div>
              </div>

              <SpecialistBookingSummary
                actionBusy={isInteractionBusy}
                actionDisabled={!canContinue}
                actionLabel={t("servicesModal.continue")}
                className={styles.desktopSummary}
                emptySelectionLabel={t(
                  isPackagesView
                    ? "servicesModal.nonePackagesSelected"
                    : "servicesModal.noneSelected",
                )}
                items={displayedSummaryItems}
                pendingParticipant={pendingGuestSummary && groupParticipantName
                  ? { id: groupSummaryParticipantId, label: groupParticipantName } : null}
                liveSelectionLabel={t(
                  isPackagesView
                    ? "servicesModal.selectedPackageCount"
                    : "servicesModal.selectedCount",
                  { count: displayedItemCount },
                )}
                onAction={continueCatalog}
                onWorkplaceNavigate={requestWorkplaceNavigation}
                rating={bookingSummaryRating}
                selectionListLabel={t(
                  isPackagesView
                    ? "servicesModal.selectedPackageCount"
                    : "servicesModal.selectedCount",
                  { count: displayedItemCount },
                )}
                showForwardArrow
                totalLabel={t("servicesModal.totalLabel")}
                totalValueLabel={displayedTotalValueLabel}
                workplaceAddress={workplace.address}
                workplaceImageAlt={workplacePresentation?.imageAlt ?? ""}
                workplaceImageSrc={workplacePresentation?.imageSrc ?? null}
                workplaceHref={workplaceHref}
                workplaceName={workplace.name}
                workplacePreviewImageSources={
                  presentation === "page"
                    ? workplacePresentation?.images?.map((image) => image.src)
                    : undefined
                }
              />
            </div>
          </DataStateBoundary>
        </div>
      </SpecialistServicesPresentationShell>

      {activeServiceConfigurationEntry &&
      activeServiceConfigurationCanRejoin &&
      activeServiceConfiguration?.kind === "bundle" &&
      activeBundleConfigurationPlan ? (
        <SpecialistServiceBundleConfigurationModal
          key={`${catalogDescendantOwnerKey}:service-bundle-configuration:${activeServiceConfigurationEntry.service.serviceId}:${activeServiceConfiguration.interactionToken}`}
          isOpen={isOpen && activeServiceConfiguration.isOpen}
          service={activeServiceConfigurationEntry.service}
          offering={activeServiceConfigurationEntry.offering}
          serviceReviewSummaries={serviceReviewSummaries}
          reviewWorkplaceName={workplace.name}
          serviceReviewsContextKind={serviceReviewsContextKind}
          fixedReviewUserLocationId={fixedReviewUserLocationId}
          servicePortfolioImages={servicePortfolioImages}
          plan={activeBundleConfigurationPlan}
          initialBundleItems={
            activeServiceConfiguration.initialBundleItems ??
            requestedSelectionByServiceId.get(
              activeServiceConfigurationEntry.service.serviceId,
            )?.bundleItems
          }
          canGoBackToDetails={activeServiceConfiguration.returnToDetails}
          disabled={
            !isReady ||
            isInteractionBusy ||
            unavailableServiceIdSet.has(
              activeServiceConfigurationEntry.service.serviceId,
            ) ||
            !isServiceConfigurationAvailable(
              activeServiceConfigurationEntry.service,
              activeServiceConfigurationEntry.offering,
            )
          }
          disabledReason={
            !isReady ||
            unavailableServiceIdSet.has(
              activeServiceConfigurationEntry.service.serviceId,
            ) ||
            !isServiceConfigurationAvailable(
              activeServiceConfigurationEntry.service,
              activeServiceConfigurationEntry.offering,
            )
              ? t("servicesModal.unavailable")
              : undefined
          }
          restoreFocusOnClose={
            !(
              pendingServiceSiblingTransition?.sourceKind === "configuration" &&
              pendingServiceSiblingTransition.fromInteractionToken ===
                activeServiceConfiguration.interactionToken
            )
          }
          returnFocusRef={serviceConfigurationReturnFocusRef}
          onClose={closeServiceConfiguration}
          onClosed={() =>
            presentPendingServiceSibling(
              "configuration",
              activeServiceConfiguration.interactionToken,
            )
          }
          onBackToDetails={returnBundleToServiceDetails}
          onConfirm={async (selection) => {
            if (!(await commitServiceConfiguration(selection))) {
              throw new Error(
                "The bundle configuration owner changed before commit.",
              );
            }
          }}
          onRetryAddOns={onRetry ? () => onRetry() : undefined}
        />
      ) : null}

      {activeServiceConfigurationEntry &&
      activeServiceConfigurationCanRejoin &&
      activeServiceConfiguration?.kind === "service" ? (
        <SpecialistServiceConfigurationModal
          key={`${catalogDescendantOwnerKey}:service-configuration:${activeServiceConfigurationEntry.service.serviceId}:${activeServiceConfiguration.interactionToken}`}
          isOpen={isOpen && activeServiceConfiguration.isOpen}
          service={activeServiceConfigurationEntry.service}
          offering={activeServiceConfigurationEntry.offering}
          reviewSummary={getPublishedLocationServiceReviewSummary(
            serviceReviewSummaries,
            activeServiceConfigurationEntry.service.serviceId,
          )}
          reviewWorkplaceName={workplace.name}
          serviceReviewsContextKind={serviceReviewsContextKind}
          fixedReviewUserLocationId={fixedReviewUserLocationId}
          servicePortfolioImages={servicePortfolioImages}
          initialStep={activeServiceConfiguration.initialStep}
          initialVariantId={activeServiceConfiguration.initialVariantId}
          priceLabelForSelection={sessionPresentation ? membershipSelectionLabel : undefined}
          benefitPresentationForSelection={serviceBenefits.getPresentation}
          initialAddOns={
            activeServiceConfiguration.initialAddOns ??
            requestedSelectionByServiceId.get(
              activeServiceConfigurationEntry.service.serviceId,
            )?.addOns
          }
          canGoBackToDetails={activeServiceConfiguration.returnToDetails}
          disabled={
            !isReady ||
            isInteractionBusy ||
            unavailableServiceIdSet.has(
              activeServiceConfigurationEntry.service.serviceId,
            ) ||
            !isServiceConfigurationAvailable(
              activeServiceConfigurationEntry.service,
              activeServiceConfigurationEntry.offering,
            )
          }
          disabledReason={
            !isReady ||
            unavailableServiceIdSet.has(
              activeServiceConfigurationEntry.service.serviceId,
            ) ||
            !isServiceConfigurationAvailable(
              activeServiceConfigurationEntry.service,
              activeServiceConfigurationEntry.offering,
            )
              ? t("servicesModal.unavailable")
              : undefined
          }
          restoreFocusOnClose={
            !(
              pendingServiceSiblingTransition?.sourceKind === "configuration" &&
              pendingServiceSiblingTransition.fromInteractionToken ===
                activeServiceConfiguration.interactionToken
            )
          }
          returnFocusRef={serviceConfigurationReturnFocusRef}
          onClose={closeServiceConfiguration}
          onClosed={() =>
            presentPendingServiceSibling(
              "configuration",
              activeServiceConfiguration.interactionToken,
            )
          }
          onBackToDetails={returnToServiceDetails}
          onConfirm={async (selection) => {
            if (!(await commitServiceConfiguration(selection))) {
              throw new Error(
                "The service configuration owner changed before commit.",
              );
            }
          }}
          onRetryAddOns={onRetry}
          onRetryVariants={onRetry}
        />
      ) : null}

      {activeServiceDetailsEntry ? (
        <SpecialistServiceDetailsModal
          key={`${catalogDescendantOwnerKey}:service-details:${activeServiceDetailsEntry.service.serviceId}:${activeServiceDetails?.interactionToken}`}
          isOpen={isOpen && activeServiceDetails?.isOpen === true}
          service={activeServiceDetailsEntry.service}
          offering={activeServiceDetailsEntry.offering}
          reviewSummary={getPublishedLocationServiceReviewSummary(
            serviceReviewSummaries,
            activeServiceDetailsEntry.service.serviceId,
          )}
          reviewWorkplaceName={workplace.name}
          serviceReviewsContextKind={serviceReviewsContextKind}
          fixedReviewUserLocationId={fixedReviewUserLocationId}
          servicePortfolioImages={servicePortfolioImages}
          durationLabel={formatDuration(activeServiceDetailsEntry.offering)}
          bundleCountLabel={getBundleCountLabel(
            activeServiceDetailsEntry.service,
          )}
          audienceLabel={getAudienceLabel(activeServiceDetailsEntry.offering)}
          priceLabel={
            activeMembershipDetailsLabel ?? (activeServiceDetailsHasBenefit
              ? exactServiceBenefitPresentation!.priceLabel
              : getPriceLabel(activeServiceDetailsEntry.offering))
          }
          benefitPresentation={serviceBenefits.getPresentation(activeServiceDetailsSelection ?? { serviceId: activeServiceDetailsEntry.service.serviceId, variantId: null })}
          variantBenefitPresentation={variantId => serviceBenefits.getPresentation({ serviceId: activeServiceDetailsEntry.service.serviceId, variantId })}
          variantPriceLabel={sessionPresentation ? (variantId) => membershipSelectionLabel({ serviceId: activeServiceDetailsEntry.service.serviceId, variantId }) : undefined}
          originalPriceLabel={activeServiceDetailsSale?.originalPriceLabel}
          savingsLabel={activeServiceDetailsSale?.savingsLabel}
          selected={
            selectedSet.has(activeServiceDetailsEntry.service.serviceId) ||
            Boolean(activeServiceDetailsSelection?.variantId)
          }
          selectedVariantId={activeServiceDetailsVariantId}
          disabled={
            !isReady || isInteractionBusy || activeServiceDetailsUnavailable
          }
          disabledReason={
            !isReady || activeServiceDetailsUnavailable
              ? t("servicesModal.unavailable")
              : undefined
          }
          restoreFocusOnClose={
            !(
              pendingServiceSiblingTransition?.sourceKind === "details" &&
              pendingServiceSiblingTransition.fromInteractionToken ===
                activeServiceDetails?.interactionToken
            )
          }
          returnFocusRef={serviceDetailsReturnFocusRef}
          onClose={closeServiceDetails}
          onClosed={() =>
            activeServiceDetails
              ? presentPendingServiceSibling(
                  "details",
                  activeServiceDetails.interactionToken,
                )
              : undefined
          }
          onToggle={() => {
            if (
              requestedSelectionByServiceId.has(
                activeServiceDetailsEntry.service.serviceId,
              )
            ) {
              void removeService(activeServiceDetailsEntry.service.serviceId);
              closeServiceDetails();
              return;
            }

            beginServiceConfiguration({
              returnToDetails: true,
              serviceId: activeServiceDetailsEntry.service.serviceId,
            });
          }}
          onCommitVariant={(variantId) =>
            beginServiceConfiguration({
              initialVariantId: variantId,
              returnToDetails: true,
              serviceId: activeServiceDetailsEntry.service.serviceId,
            })
          }
          onRetryVariants={onRetry}
        />
      ) : null}

      {activePackageDetailsEntry ? (
        <SpecialistPackageDetailsModal
          key={`${catalogDescendantOwnerKey}:package-details:${activePackageDetailsEntry.packageId}`}
          isOpen={isOpen && activePackageDetails?.isOpen === true}
          packageItem={activePackageDetailsEntry}
          benefitPlan={packageBenefits.snapshot.status === "ready" ? packageBenefits.snapshot.plans.get(activePackageDetailsEntry.packageId) ?? null : null}
          validityLabel={getPackageValidityLabel(activePackageDetailsEntry)}
          appointmentCountLabel={getPackageAppointmentCountLabel(
            activePackageDetailsEntry,
          )}
          includedLabel={t("profile.packageIncludedLabel")}
          priceLabel={getPriceLabelForPrice(activePackageDetailsEntry.price)}
          originalPriceLabel={activePackageDetailsSale?.originalPriceLabel}
          savingsLabel={activePackageDetailsSale?.savingsLabel}
          selected={selectedPackageIdSet.has(
            activePackageDetailsEntry.packageId,
          )}
          disabled={!isReady || isInteractionBusy || !canPurchasePackage(activePackageDetailsEntry)}
          disabledReason={!canPurchasePackage(activePackageDetailsEntry) ? tAll("packagePurchase.payment.unavailableTitle") : !isReady ? t("servicesModal.unavailable") : undefined}
          returnFocusRef={packageDetailsReturnFocusRef}
          onClose={closePackageDetails}
          onToggle={() =>
            void togglePackage(activePackageDetailsEntry.packageId)
          }
        />
      ) : null}

      <CheckoutConfirmationModal
        isOpen={isOpen && pendingDismissReason !== null}
        title={t("servicesModal.leaveConfirmation.title")}
        message={t("servicesModal.leaveConfirmation.message")}
        secondaryLabel={t("servicesModal.leaveConfirmation.cancel")}
        primaryLabel={t("servicesModal.leaveConfirmation.confirm")}
        onClose={cancelSelectedServiceDismissal}
        onSecondaryAction={cancelSelectedServiceDismissal}
        onPrimaryAction={confirmSelectedServiceDismissal}
      />
    </>
  );
}
